import React, {
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { AppDispatch, RootState } from "../../../redux/store";
import { clearTraditionalQuoteData } from "../../../redux/reducer/locationSlice/locationSlice";
import GetQuote from "../../../components/getQuote/GetQuote";
import { MutliDropdown, MultiSelectChips, SingleDropdown } from "../../../components/shared";
import { SingleDropdownItemType } from "../../../components/shared/SingleDropdown";
import AddDocumentURLModal from "../../../components/modals/AddDocumentURLModal";
import {
  cmToInch,
  kgToLbs,
  onInputPhNumFormat,
  expiredData,
  getISODate,
  getDockTimeInUTC,
  extractTime,
  storedTimeToPicker,
  isPastPickupDate,
  pickerTimeToStored,
  formatTimeForDisplay,
} from "../../../utils/utils";

import {
  validateZipCode,
  validateContactEmail,
  ValidateDate,
  ValidateExpedietedDate,
  ValidateExpedietedCrrentTime,
  validateMaskedPhoneNumber,
} from "../../../utils/validation";
import {
  GET_DATA_FROM_ZIPCODE,
  SAVE_QUOTATION,
  SAVE_LTL_QUOTATION,
  PHONE_VALIDATE_API,
} from "../../../apis/end-points";
import {
  customerOrderColumns,
  agentOrderColumns,
  CustomerRowDataType,
  AgentRowDataType,
  DataTypes,
  DocumentInfo,
  Fields,
  FormErrorsType,
  GetQuoteApiResponse,
  MappedService,
  orderTypeColumns,
  OrderTypeRowDataType,
  QuoteInfo,
  ServiceLevelType,
  tableConfig,
  MoreReferencesType,
  GetLTLQuoteApiResponse,
  getLTLQuotesMandatoryFields,
  GetLTLFinalMileQuoteApiResponse,
  accessorialsColumn,
  hubData,
} from "./CustomerOrder-types";

import { getApi, postApi } from "../../../apis/api";
import {
  ORDER_REFERENCE_2_INDEX,
  OrderReference2ConfigMap,
  fetchOrderReference2Config,
  getOrderReference2Value,
  isOrderReference2Locked,
  resolveOrderReference2Source,
} from "./orderReference.utils";
import { Loader } from "../../../components/loaders/loaders";
import { useMessage } from "../../../context/MessageContext";
import EmailChips from "../../../components/shared/EmailChips";
import useFormChangeHandler from "../../../hooks/useFormChangeHandler";
import useMandatoryFieldFocus from "../../../hooks/useMandatoryFieldFocus";
import {
  DROPDOWN_FIELD_KEYS,
  HANDLING_UNITS_FIELD_KEY,
} from "../../../utils/constants";
import {
  isMandatoryMessage,
  pruneItemIssues,
  prunePackageIssues,
  validateOrderForm,
} from "../../../utils/orderValidation";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import { CUSTOMER_ID } from "../../../utils/authUtils";

import { AddIcon, DeleteIcon } from "../../../assets";
import {
  GENERAL_ERROR_MESSAGE,
  HANDLING_UNITS_REQUIRED_MESSAGE,
  ORDER_MANAGEMENT_MESSAGES,
  PICKUP_DATE_PAST_ERROR,
} from "../../../utils/message";
import GetLTLQuote from "../../../components/getQuote/GetLTLQuote";
import OrderItemDetailTable from "../../../components/shared/table/OrderItemDetailTable";
import { DEFAULT_DOCK_CLOSE_TIME, DEFAULT_DOCK_OPEN_TIME, EXPEDIET_PACKAGE_TYPE, PACKAGE_TYPE, PHONENUMBER } from "../../../utils/constants";
import moment from "moment";
import CustomTimePicker from "../../../components/CustomTimePicker";
import { useSvgRef } from "@mui/x-charts";
import GetTraditionalQuote, {
  filterLtlOnlyQuotes,
} from "../../../components/getQuote/GetTraditionalQuote";
import AddressDropDown from "../../../components/shared/AddressDropDown";

interface ColumnTypes {
  key: string;
  title: string;
}

const agentHubColumns: ColumnTypes[] = [{ key: "name", title: "Name" }];
const serviceColumns: ColumnTypes[] = [{ key: "name", title: "Service Level" }];

interface AgentHubDropdown {
  id?: string;
  name: string | null;
  agentHubNo: string;
  isVirtualHub?: boolean;
  masterHubId?: string | null;
  masterHubName?: string;
}
interface DropwdownValueTypes {
  id: string;
  name: string;
}
const AdditionalServices = [
  "Additional Services",
  "Remove & Disposal",
  "Order Swap & Exchange",
];
interface OrderProps {
  quoteTypeRadio?: any;
  setQuoteType?: (data: any) => void;
}

/**
 * CustomerOrder Component
 *
 * This component allows users to create a new customer order by filling out a form with various fields,
 * including destination address, service level, item details, and more. The form supports validation,
 * fetching data from APIs, and submitting the order data to the backend.
 */
const TraditionalOrder: React.FC<OrderProps> = ({
  quoteTypeRadio,
  setQuoteType,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setMessage } = useMessage();

  // Get Traditional Quote data from Redux if available, otherwise fall back to location state
  const { traditionalQuoteData } = useSelector(
    (state: RootState) => state.locationState
  );
  const { state: locationState } = location;
  const state = traditionalQuoteData || locationState;
  const referenceLimit = 5;
  const { countryCode, orderTypes } = useSelector(
    (state: RootState) => state.staticData
  );
  const { isUserOfOmniMove, userRole, isCustomer, customerLevel } = useSelector(
    (state: RootState) => state.auth
  );
  const resetLocationState = () => {
    setIsSelectedParent(false);
    // Clear the Redux state for Traditional Quote data
    dispatch(clearTraditionalQuoteData());
    // Clear the location state by navigating to the same route with empty state
    navigate(location.pathname, {
      state: null,
      replace: true, // This replaces the current history entry instead of adding a new one
    });
  };
  const [originCities, setOriginCities] = useState<string[]>([]);
  const [destinationCities, setDestinationCities] = useState<string[]>([]);
  // boolean states
  const [loading, setLoading] = useState<boolean>(false);
  const [groupQuoteId, setGroupQuoteId] = useState<string>(state?.id ?? "");
  const [stateQuoteId, setStateQuoteId] = useState<string>(
    state?.quoteId ?? ""
  );
  const [getQuote, setGetQuote] = useState<boolean>(false);
  const [isQuickQuoteDataReady, setIsQuickQuoteDataReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const originPhoneRef = useRef<HTMLInputElement>(null);
  const destinationPhoneRef = useRef<HTMLInputElement>(null);
  const [savedData, setSavedData] = useState<any>(null);
  const [hasMasterHub, setHasMasterHub] = useState(false);
  const [masterHubName, setMasterHubName] = useState("");
  const [masterHubAddress, setMasterHubAddress] = useState<any>({});
  const addressColumns = [{ key: "address", title: "" }];
  const [destAddressList, setDestAddressList] = useState<any[]>([]);
  const [destAddressSelected, setDestAddressSelected] = useState<any>(null);
  const [originAddressList, setOriginAddressList] = useState<any[]>([]);
  const [addressSelectedOrigin, setAddressSelectedOrigin] = useState<any>({});
  const [addressSelected, setAddressSelected] = useState<any>({});
  const [ltlFMData, setLtlFMData] = useState<any>(null);
  const [quickQuoteLtlData, setQuickQuoteLtlData] = useState<any>(null);
  const [quickQuoteLtlFMData, setQuickQuoteLtlFMData] = useState<any>(null);
  const [selectedHubData, setSelectedHubData] = useState<hubData>({
    operationFrom: "",
    operationTo: "",
    hubName: "",
  });

  // modal States
  const [viewAddDocumentModal, setViewAddDocumentModal] =
    useState<boolean>(false);
  // Add item details table data states
  const [data, setData] = useState<DataTypes[]>([
    {
      id: "",
      serial: 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: "IN",
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      package: [],
      stackable: "No",
      hazmat: false,
    },
  ]);
  // get quotes table data states
  const [quoteData, SetQuoteData] = useState<
    { info: QuoteInfo; services: MappedService[] }[]
  >([]);
  const [serviceSelected, setServiceSelected] = useState<any>({});
  const [serviceList, setServiceList] = useState<DropwdownValueTypes[]>([]);
  const [showLTLList, setShowLTLList] = useState<boolean>(false);
  // "LTL Only" flag chosen on the quote panel; sent with LTL orders and LTL saved quotes.
  const [isLtlOnly, setIsLtlOnly] = useState<boolean>(false);
  const [showFMList, setShowFMList] = useState<boolean>(false);
  const [showLTLData, setShowLTLData] = useState<boolean>(false);
  const [ltlFMQuoteSelected, setLtlFMQuoteSelected] = useState<boolean>(false);
  const [TLList, setLTLList] = useState<any[]>([]);
  const [isLtlFinalmile, setIsLTLFinalMile] = useState(false);
  const ltlOrderType: any = orderTypes.find(
    (order: any) => order?.name === "LTL"
  );
  const ltlFMOrderType: any = orderTypes.find(
    (order: any) => order?.name === "LTL + Final Mile"
  );
  const [saveQuotationPage, setSaveQuotationPage] = useState(false);
  // fields states, errors states
  const [fields, setFields] = useState<Fields>({
    orderReference: "",
    orderReference2: "",
    destinationAddress: "",
    destinationAPTSuite: "",
    destinationZipcode: "",
    destinationCity: "",
    destinationState: "",
    destinationCountry: "",
    destinationBusinessLocation: "",
    destinationConsigneeName: "",
    destinationConsigneePhoneNumber: "",
    destinationConsigneePhoneExt: "+1",
    destinationConsigneeContactEmail: "",
    OriginAddress: "",
    OriginAPTSuite: "",
    OriginZipcode: "",
    OriginCity: "",
    OriginState: "",
    OriginCountry: "",
    OriginBusinessLocation: "",
    OriginConsigneeName: "",
    OriginConsigneePhoneNumber: "",
    OriginConsigneePhoneExt: "+1",
    OriginConsigneeContactEmail: "",
    LTLPickupAccessorials: [],
    PickUpDockName: "",
    PickUpDOckNumber: "",
    PickUpDockDate: moment().format("YYYY-MM-DD"),
    PickUpDockTime: "08:00 AM",
    PickUpDockOpenTime: DEFAULT_DOCK_OPEN_TIME,
    PickUpDOckCloseTime: DEFAULT_DOCK_CLOSE_TIME,
    PickUpDOckNote: "",
    PickUpFCFS: false,
    LTLDropAccessorials: [],
    DropDockName: "",
    DropDOckNumber: "",
    DropDockDate: "",
    DropDockTime: "",
    DropDockOpenTime: DEFAULT_DOCK_OPEN_TIME,
    DropDOckCloseTime: DEFAULT_DOCK_CLOSE_TIME,
    DropDOckNote: "",
    DropFCFS: false,
    markup: 0,
    totalLinearFeet: "0.00",
    totalDensity: "0.00",
    totalCBM: "0.00",
    destinationExtramen: "",
    destinationNoOfFlights: "",
    destinationWaitTimeMinutes: "",
    destinationStorage: "",
    destinationHandingUnits: "",
    destinationWeight: "",
    destinationPieces: "",
    destinationSpecialInstructions: "",
    MidStopAddress: "",
    MidStopAPTSuit: "",
    MidStopZipcode: "",
    MidStopCity: "",
    MidStopState: "",
    MidStopCountry: "",
    MidStopContactName: "",
    MidStopPhoneNumber: "",
    MidStopPhoneNumberExt: "+1",
    courierPro1: "",
    courierPro2: "",
    plProvider: "",
    MidStopContactEmail: [],
    CarrierName: "",
    serviceCost: "",
    costDetails: [],
    scac: "",
    distance: "",
    offerId: "",

    productTransactionId: "",
    agentHubId: "",
    selectedServiceId: "",
    selectedQuoteId: "",
    customerMarkupType: "",
    allQuoteId: [],
    orderSwapAndExchange: false,
    additionalServices: [],
    removeAndDisposal: false,
    PickupTargetDateFrom: "",
    PickupTargetDateTo: "",
    PickupTargetTimeFrom: "",
    PickupTargetTimeTo: "",
    PickupDeliveryDateFrom: "",
    PickupDeliveryDateTo: "",
    PickupDeliveryTimeFrom: "",
    PickupDeliveryTimeTo: "",
    AgentHub: {
      id: "",
      name: "",
    },
    originCity: "",
    finalMileDistance: "",
    finalMileCost: "",
    totalDensityView: "",
    totalLinearFeetView: "",
    totalLinearCBMView: "",
    quoteId: "",
    orderFor: "",
    source: "",
    loadId: "",
    internalLTL: false,
  });
  const [errors, setErrors] = useState<FormErrorsType>({});

  // Mandatory-field validation: highlights everything missing when the user asks for a
  // quote or saves, then scrolls to and focuses the control highest on the page.
  const [invalidCells, setInvalidCells] = useState<Record<number, string[]>>({});
  const [invalidPackageCells, setInvalidPackageCells] = useState<
    Record<number, Record<number, string[]>>
  >({});
  const [itemsEmpty, setItemsEmpty] = useState(false);
  const [focusCell, setFocusCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const { applyValidation, clearMandatory } = useMandatoryFieldFocus({
    containerRef,
    setErrors,
    values: fields,
  });
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo>({
    files: [],
    urls: [],
  });

  // added for button able and disable
  const [isSelectedParent, setIsSelectedParent] = useState<boolean>(false);

  // dropdown data and selected row states
  const [parentCustomerId, setParentCustomerId] = useState<string>();
  const [customerRowData, setCustomerRowData] = useState<CustomerRowDataType>({
    id: "",
    name: "",
    customerNumber: "",
    customerLevel: "",
  });
  const [apiPackageType, setApiPackageType] = useState<any>([]);
  const handleTimeChange = (time: string, property: string) => {
    if (property === "DropDockOpenTime") dropHoursAreDefaultRef.current.open = false;
    if (property === "DropDOckCloseTime") dropHoursAreDefaultRef.current.close = false;
    setFields((prev) => ({
      ...prev,
      [property]: time,
    }));
    compareExpediteDates({ ...fields, [property]: time });
    if (property === "PickUpDockTime") {
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }
    // check error
    let error = "";
    switch (property) {
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${convertTo24HourFormat(
              fields.DropDockTime
            )}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${convertTo24HourFormat(time)}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${convertTo24HourFormat(
                fields.PickUpDockTime
              )}:00`
            );
          }
        }
        break;
      default:
        break;
    }
    if (
      !(
        property === "PickupTargetTimeFrom" ||
        property === "PickupTargetTimeTo" ||
        property === "PickupDeliveryTimeFrom" ||
        property === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [property]: error,
        };
      });
    }
  };
  const [assCustomerRowData, setAssCustomerRowData] =
    useState<CustomerRowDataType>({
      id: "",
      name: "",
      customerNumber: "",
      customerLevel: "",
    });
  const [orderTypeRowData, setOrderTypeRowData] =
    useState<OrderTypeRowDataType>({
      id: "",
      name: "",
      description: "",
    });
  // LTL + Final Mile drop dock hours are sent exactly as entered (unchanged behaviour);
  // every other order type sends them as 24-hour HH:mm like the pickup hours.
  const dropTimeForPayload = (time?: string) =>
    orderTypeRowData?.name === "LTL + Final Mile" ? time || "" : pickerTimeToStored(time);
  // Which drop dock hours still hold the default this form filled in (see the order-type effect).
  const dropHoursAreDefaultRef = useRef({ open: true, close: true });
  const [customerNameDropdown, setCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [assCustomerNameDropdown, setAssCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [orderTypeDropdown, setOrderTypeDropdown] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorials, setDropAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorialsSelected, setDropAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [pickupAccessorials, setPickupAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupAccessorialsSelected, setPickupAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [dropLimitedAccess, setDropLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropLimitedAccessSelected, setDropLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();
  const [pickupLimitedAccess, setPickupLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupLimitedAccessSelected, setPickupLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();
  const [ltlAccessorialData, setLtlAccessorialData] = useState<any>(null);

  // Carried into the "Select" redirect when an LTL quote resolves to LTL + Final Mile
  // (ViewDetailsTable reads this prop and puts it on navigation state), so the
  // Drop Accessorials / Limited Access selections survive the hand-off to the LTL+FM
  // view instead of coming back empty. Mirrors QuickQuote.tsx's identical effect.
  useEffect(() => {
    setLtlAccessorialData({
      dropAccessorialsSelected: dropAccessorialsSelected,
      dropLimitedAccessSelected: dropLimitedAccessSelected,
      pickupLimitedAccessSelected: pickupLimitedAccessSelected,
    });
  }, [
    dropAccessorialsSelected,
    dropLimitedAccessSelected,
    pickupLimitedAccessSelected,
  ]);

  const [notLTL, setNotLTL] = useState<boolean>(true);
  const [agentNameDropdown, setAgentNameDropdown] = useState<
    AgentRowDataType[]
  >([]);
  const [agentRowData, setAgentRowData] = useState<AgentRowDataType>({
    id: "",
    name: "",
    agentType: "",
  });
  const [dropdownValues, setDropdownValues] = useState<{
    [key: string]: SingleDropdownItemType | null;
  }>({
    hubName: null,
  });
  const [agentHubDropdownData, setAgentHubDropdownData] = useState<
    AgentHubDropdown[]
  >([]);
  const [agentHubRowData, setAgentHubRowData] = useState<AgentHubDropdown>();
  // unsave model
  const {
    checkInputChange,
    handleNavigation,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleCustomerOrderExit,
    handleUpdateNavigation,
    showModal,
    isFormChanged,
  } = useFormChangeHandler();
  const [isQuote, setIsQuote] = useState<boolean>(false);
  const [associatedCusReqired, setAssociatedCusReqired] = useState<boolean>(
    !isUserOfOmniMove && (isCustomer || userRole === "manager" || userRole === "employee" || userRole === "admin") ? false : true
  );
  const [moreReferences, setMoreReferences] = useState<MoreReferencesType[]>([
    {
      referenceCount: 2,
      referenceValue: "",
    },
  ]);

  // Reference 2 carried by a loaded quote, or null when the form was not populated
  // from one. While it is set the loaded value survives, unless the governing
  // customer has the setting on and overrides it. Cleared as soon as the user picks
  // a customer themselves, at which point that customer alone decides the value.
  const loadedOrderReference2Ref = useRef<string | null>(null);

  /** User changed a customer selection — the loaded record no longer applies. */
  const clearLoadedOrderReference2 = () => {
    loadedOrderReference2Ref.current = null;
  };

  /**
   * Origin/destination addresses come from the selected customer's address book, so they
   * no longer apply once the user picks a different customer. Only called from the
   * dropdown handlers (never from quote loads) so prefilled quote addresses survive.
   */
  const resetCustomerLinkedAddresses = () => {
    setAddressSelectedOrigin({});
    setAddressSelected({});
    setOriginAddressList([]);
    setDestAddressList([]);
    setFields((prev) => ({
      ...prev,
      OriginAddress: "",
      OriginAddressId: "",
      OriginAPTSuite: "",
      OriginZipcode: "",
      OriginCity: "",
      OriginState: "",
      OriginCountry: "",
      OriginBusinessLocation: "",
      OriginConsigneeName: "",
      OriginConsigneePhoneNumber: "",
      OriginConsigneePhoneExt: "+1",
      OriginConsigneeContactEmail: "",
      destinationAddress: "",
      destinationAddressId: "",
      destinationAPTSuite: "",
      destinationZipcode: "",
      destinationCity: "",
      destinationState: "",
      destinationCountry: "",
      destinationBusinessLocation: "",
      destinationConsigneeName: "",
      destinationConsigneePhoneNumber: "",
      destinationConsigneePhoneExt: "+1",
      destinationConsigneeContactEmail: "",
    }));
  };

  // Live Order Reference 2 configuration read from the customer endpoint when the
  // form is populated from a quote. Rows selected through a dropdown already carry
  // the setting (see extraFields), so this stays empty for normal order creation.
  const [orderReference2Config, setOrderReference2Config] =
    useState<OrderReference2ConfigMap>({});

  // Quote loads set the customer rows from the quote payload, which has no Order
  // Reference 2 setting. Merge the configuration in as soon as it is available —
  // whichever arrives last — so the prefill effect below can act on it.
  useEffect(() => {
    if (!customerRowData?.id || customerRowData?.isUsedOrderReference2 !== undefined) return;
    const config = orderReference2Config[customerRowData.id];
    if (config) setCustomerRowData({ ...customerRowData, ...config });
  }, [customerRowData?.id, orderReference2Config]);

  useEffect(() => {
    if (!assCustomerRowData?.id || assCustomerRowData?.isUsedOrderReference2 !== undefined) return;
    const config = orderReference2Config[assCustomerRowData.id];
    if (config) setAssCustomerRowData({ ...assCustomerRowData, ...config });
  }, [assCustomerRowData?.id, orderReference2Config]);

  // Customer that governs Order Reference 2 (sub-customer wins over its parent).
  const orderReference2Source = useMemo(
    () =>
      customerRowData
        ? resolveOrderReference2Source(customerRowData, assCustomerRowData)
        : null,
    [customerRowData, assCustomerRowData]
  );

  // When the governing customer has "Use Order Reference 2" enabled, the value is
  // fixed by their configuration and cannot be edited or removed on the order.
  const isReference2Locked = isOrderReference2Locked(orderReference2Source);

  useEffect(() => {
    // Clear field when customer is explicitly removed
    if (customerRowData === null) {
      setMoreReferences((prev) => {
        const updated = [...prev];
        if (updated.length > ORDER_REFERENCE_2_INDEX) {
          updated[ORDER_REFERENCE_2_INDEX] = {
            ...updated[ORDER_REFERENCE_2_INDEX],
            referenceValue: "",
          };
        }
        return updated;
      });
      return;
    }

    // Only act when the customer object carries the setting (isUsedOrderReference2
    // explicitly defined). Edit-load flows set a partial object without this key
    // (undefined), so we skip to avoid wiping the reference2 loaded from the order.
    if (!orderReference2Source) return;

    const prefillValue = getOrderReference2Value(
      orderReference2Source,
      loadedOrderReference2Ref.current
    );
    setMoreReferences((prev) => {
      const updated = [...prev];
      if (updated.length > ORDER_REFERENCE_2_INDEX) {
        updated[ORDER_REFERENCE_2_INDEX] = {
          ...updated[ORDER_REFERENCE_2_INDEX],
          referenceValue: prefillValue,
        };
      }
      return updated;
    });
  }, [customerRowData, assCustomerRowData, orderReference2Source]);

  //Normalize date by converting to utc
  const normalizeDate = (date: any) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const getUTCDate = (dateStr: string, timeStr: string): string => {
    const combinedDateTime = moment(
      `${dateStr} ${timeStr}`,
      "YYYY-MM-DD hh:mm:ss A"
    );
    return combinedDateTime.isValid()
      ? combinedDateTime.toISOString()
      : "Invalid date or time";
    // const newDate = moment.utc(`${dateStr.split("T")[0]} ${timeStr}`).format('YYYY-MM-DDTHH:mm:ss')+"Z";
    // return newDate;
  };

  const getUTCTime = (dateStr: string, timeStr: string): string => {
    let newDate = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    const newTime = moment.utc(`${newDate} ${timeStr}`).format("hh:mm A");
    return newTime;
  };

  const mapLTLItems = (data: any[]) => {
    return data.map((item) => ({
      billedDimension: {
        length: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.length || ""
              : cmToInch(item.itemDimension?.length),
          unit: "IN",
        },
        width: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.width || ""
              : cmToInch(item.itemDimension?.width),
          unit: "IN",
        },
        height: {
          value:
            item?.unit === "in" || item?.unit?.name === "in"
              ? item.itemDimension?.height || ""
              : cmToInch(item.itemDimension?.height),
          unit: "IN",
        },
      },
      isMixedClass: false,
      isStackable:
        item?.stackable == "Yes" ||
          item?.stackable?.name == "Yes" ||
          item?.stackable?.name == "yes"
          ? true
          : false || false,
      marksAndNumbers: null,
      linearFeet: item?.linearFeet?.toString(),

      packagingType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      quantity: parseInt(item.quantity) || 0,
      referenceList: mapReferences(false),
      shippedItemList: [
        {
          commodityClass: item.itemClass.toString() || "5",
          commodityDescription: item.description || "",
          commodityType: null,
          dimensions: {
            length: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.length || ""
                  : cmToInch(item.itemDimension?.length),
              unit: "IN",
            },
            width: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.width || ""
                  : cmToInch(item.itemDimension?.width),
              unit: "IN",
            },
            height: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.height || ""
                  : cmToInch(item.itemDimension?.height),
              unit: "IN",
            },
          },
          isHazMat: item.hazmat,
          hazMatItemInfo: item.hazmat
            ? {
              hazIdentificationNbr: item.hazardMaterial?.unno || "",
              hazProperShippingName: item.hazardMaterial?.itemName || "",
              hazClassType: item.hazardMaterial?.hazmatClass || "",
              hazPackingGroupType: item.hazardMaterial?.packagingGroup || "",
              hazEmergencyPhoneNbr:
                item.hazardMaterial?.phoneNumber.replace(/\D/g, "") || "",
              hazEmergencyName: item.hazardMaterial?.contact || "",
              hazPlacardRequiredFlag: false,
              hazPlacardDetails: "Placard Details",
              hazERR: "ERR Number",
              hazFlashpointTemperature: {
                value: 1,
                unit: "F",
              },
              hazAdditionalDetails: "Additional Haz Details",
            }
            : {},
          name: "",
          NMFCDescription: null,
          NMFCNbr: item.nmfc || "",
          packagingType: item.packageType || "",
          quantity: parseInt(item.quantity) || 0,
          weight: {
            value:
              item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
                ? item.weight || ""
                : kgToLbs(item.weight),
            unit: "LB",
          },
        },
      ],
      sortAndSegregateFlag: true,
      weight: {
        value:
          item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
            ? item.weight || ""
            : kgToLbs(item.weight),
        unit: "LB",
      },
    }));
  };

  const mapReferences = (shipment: boolean) => {
    let references = [
      {
        type: `${shipment ? "Shipment " : ""}Reference 1`,
        value: fields.orderReference,
        isPrintAsBarCode: true,
      },
    ];

    if (moreReferences.length > 0) {
      moreReferences.map((ref, index) => {
        if (ref["referenceValue"] != "") {
          references.push({
            type: `${shipment ? "Shipment " : ""}Reference ${index + 2}`,
            value: ref["referenceValue"],
            isPrintAsBarCode: false,
          });
        }
      });
    }
    return references;
  };

  const getTraditionalQuoteData = async () => {
    if (!runValidation("getLtlQuote")) return;
    // Reset common state like in LTL flow
    resetLocationState();
    setIsSelectedParent(false);
    setShowLTLList(false);
    setShowLTLData(false);
    setShowFMList(false);
    clearFinalMileQuoteDetails();
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
    setGetQuote(false);

    setStateQuoteId("");
    setShowLTLData(false);
    setShowFMList(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));

    // ------- helpers -------
    const asLbs = (wt: number | string | undefined) => {
      const num = Number(wt || 0);
      // return data?.[0]?.unit === "in" ? num : kgToLbs(num);
      const weightUnit = data?.[0]?.weightUnit?.name?.toLowerCase();

      // If weight is in kgs, convert to lbs; otherwise assume it's already in lbs
      return weightUnit === "kgs" ? kgToLbs(num) : num;
    };

    const addrLineList = (addr?: string, apt?: string) => {
      if (addr && apt) return [addr, apt];
      if (addr) return [addr];
      return [];
    };

    const minDim = (n: number) => (n && n > 0 ? n : 1); // avoid 0-sized dims
    const normalizePkgType = (val: any) => {
      if (!val) return "BALE";
      if (typeof val === "string") return val;
      if (typeof val === "object") return val?.name ?? val?.id ?? "BALE";
      return String(val);
    };

    // === NEW: packaging helpers (minimal) ===
    // normalize to singular, uppercase enum string (e.g., "BAGS" -> "BAG")
    const toPkgString = (val: any) => {
      const raw =
        typeof val === "string"
          ? val
          : typeof val === "object" && (val?.name || val?.id)
            ? val.name || val.id
            : "BAG";
      const up = String(raw).trim().toUpperCase();
      const map: Record<string, string> = {
        BAGS: "BAG",
        BAG: "BAG",
        BOXES: "BOX",
        BOX: "BOX",
        BALES: "BALE",
        BALE: "BALE",
        PALLETS: "PALLET",
        PALLET: "PALLET",
        DRUMS: "DRUM",
        DRUM: "DRUM",
        CARTONS: "CARTON",
        CARTON: "CARTON",
        CRATES: "CRATE",
        CRATE: "CRATE",
      };
      return map[up] || up;
    };

    // object form for shippedItemList.packagingType
    const toPkgObject = (val: any) => {
      const s = toPkgString(val);
      return { id: s, name: s };
    };
    // === end NEW helpers ===

    // Map UI line-items (data) -> traditional items payload
    const mapTraditionalItems = (rows: any[] = []) =>
      rows.map((row: any) => {
        const unit = String(row?.unit ?? row?.Unit ?? "in").toLowerCase(); // "in" | "cm"
        const qty = Number(row?.quantity ?? row?.Quantity ?? 0);
        const handlingUnit = Number(
          row?.handlingUnit ?? row?.HandlingUnit ?? qty ?? 0
        );

        const toInches = (val: number) =>
          unit === "cm" ? Number((val / 2.54).toFixed(3)) : val;

        const rawL = Number(row?.itemDimension?.length ?? 0);
        const rawW = Number(row?.itemDimension?.width ?? 0);
        const rawH = Number(row?.itemDimension?.height ?? 0);

        const lengthIn = toInches(minDim(rawL));
        const widthIn = toInches(minDim(rawW));
        const heightIn = toInches(minDim(rawH));

        // weight per piece -> string
        const wt = asLbs(row?.weight ?? row?.Weight ?? 0);

        return {
          description: row?.description ?? "",
          handlingUnit: String(handlingUnit), // string per sample
          quantity: String(qty), // string per sample
          weight: String(wt), // string per sample
          weightUnit: "lbs",
          length: String(lengthIn), // string per sample
          width: String(widthIn), // string per sample
          height: String(heightIn), // string per sample
          itemClass: String(row?.itemClass ?? row?.Class ?? "300"),
          comments: row?.comments ?? "",
          nmfc: row?.nmfc ?? "",
          skuNumber: row?.skuNumber ?? "",
          // packageType: toPkgString(
          //   normalizePkgType(row?.packageType ?? row?.PackageType)
          // ),
          packageType: row?.packageType,
          package: Array.isArray(row?.package) ? row?.package : [],
          stackable: Boolean(row?.stackable ?? row?.Stackable ?? false),
          linearFeetStackable: Number(row?.linearFeetStackable ?? 0),
          linearFeet: Number(row?.linearFeet ?? row?.LinearFeet ?? 0), // number
          density: Number(row?.density ?? row?.Density ?? 0), // number
          cbm: Number(row?.cbm ?? row?.CBM ?? 0), // number
          hazardMaterial: row?.hazardMaterial ?? {},
        };
      });

    // Compute a consistent total weight (LB) from items (qty * per-piece weight)
    const computeTotalWeightLb = (rows: any[] = []) => {
      let total = 0;
      rows.forEach((row: any) => {
        const qty = Number(row?.quantity ?? row?.Quantity ?? 1);
        const perPiece = asLbs(row?.weight ?? row?.Weight ?? 0);
        total += qty * perPiece;
      });
      // Fallback to destinationWeight if items don’t have weights
      if (total <= 0) total = asLbs(fields?.destinationWeight || 0);
      return Number(total.toFixed(2));
    };

    // ------- payload -------

    const items = mapTraditionalItems(data);
    const shipmentTotalWeight = computeTotalWeightLb(data);

    // === NEW: post-process mapLTLItems to enforce packaging formats
    const handlingUnitList = mapLTLItems(data)?.map((hu: any) => ({
      ...hu,
      // top-level HU packagingType must be a STRING (e.g., "BAG")
      packagingType: hu?.packagingType,
      // packagingType: toPkgString(hu?.packagingType),
      // shipped item packagingType must be OBJECT {id,name}
      shippedItemList: (hu?.shippedItemList || []).map((si: any) => ({
        ...si,
        packagingType: si?.packagingType,
        // packagingType: toPkgObject(si?.packagingType),
      })),
    }));
    // === end NEW

    // ---------- SAFE TIME HELPERS ----------
    const isBad = (v?: string | null) =>
      !v ||
      typeof v !== "string" ||
      v.trim() === "" ||
      v.toLowerCase().includes("invalid");

    const safeConvertTo24Hour = (val?: string) => {
      const input = typeof val === "string" ? val : "";
      return typeof convertTo24HourFormat === "function"
        ? convertTo24HourFormat(input)
        : input;
    };

    const coalesceTime = (
      input?: string,
      fallback?: string,
      defaultVal?: string
    ): string => {
      const t = safeConvertTo24Hour(input);
      if (!isBad(t)) return t!;
      const tfb = safeConvertTo24Hour(fallback);
      if (!isBad(tfb)) return tfb!;
      return defaultVal || "00:00";
    };

    // Prepare pickup/drop dock data
    const uiISO = getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || "");

    const pickUpISODate = !isBad(uiISO)
      ? (uiISO as string)
      : new Date().toISOString();

    const pickUpDisplayTime = fields.PickUpDockTime || "08:00 AM";

    const pickOpen = coalesceTime(
      fields.PickUpDockOpenTime,
      "",
      "00:00"
    );
    const pickClose = coalesceTime(
      fields.PickUpDOckCloseTime,
      "",
      "23:59"
    );
    const dropOpen = coalesceTime(
      (fields as any).DropDockOpenTime,
      "",
      "00:00"
    );
    const dropClose = coalesceTime(
      (fields as any).DropDOckCloseTime,
      "",
      "23:59"
    );

    const pu = new Date(pickUpISODate);
    const safeDropDate = (() => {
      if (isNaN(pu.getTime())) return new Date().toISOString().slice(0, 10);
      const d = new Date(pu);
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().slice(0, 10);
    })();

    const body = {
      quoteDetail: {
        customerId: assCustomerRowData?.id
          ? assCustomerRowData?.id
          : customerRowData?.id,
        quoteFromScreen: "order",

        items,

        destinationAddress: {
          address: fields?.destinationAddress || "",
          aptSuit: fields?.destinationAPTSuite || "",
          zipCode: fields?.destinationZipcode || "",
          city: fields?.destinationCity || "",
          state: fields?.destinationState || "",
          country: fields?.destinationCountry || "",
          businessLocation: fields?.destinationBusinessLocation || "",
          contactName: fields?.destinationConsigneeName || "",
          phoneNumber: fields?.destinationConsigneePhoneNumber
            ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
            : "",
          countryCallingCode: fields?.destinationConsigneePhoneExt || "+1",
          contactEmail: fields?.destinationConsigneeContactEmail || "",
          extraMen: fields?.destinationExtramen || "",
          waitTime: fields?.destinationWaitTimeMinutes || "",
          stairsNumber: fields?.destinationNoOfFlights || "",
          storage: fields?.destinationStorage || "",
          handlingUnit: Number(fields?.destinationHandingUnits || 0),
          weight: Number(fields?.destinationWeight || 0),
          pieces: Number(fields?.destinationPieces || 0),
          specialInstructions: fields?.DropDOckNote || "",
        },

        additionalServices: Array.isArray(fields?.additionalServices)
          ? fields.additionalServices
          : [],
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: pickUpISODate,
          pickUpTime: pickUpDisplayTime,
          note: fields.PickUpDOckNote || "",
          dockOpenTime: pickOpen,
          dockCloseTime: pickClose,
          dockOpenTimeInUTC: getDockTimeInUTC(pickUpISODate, pickOpen),
          dockCloseTimeInUTC: getDockTimeInUTC(pickUpISODate, pickClose),
          firstComeFirstServe: !!fields.PickUpFCFS,
        },

        dropDock: {
          dockName: fields.DropDockName || "",
          pickupNumber: fields.DropDOckNumber || "",
          dropDate: safeDropDate,
          dropTime: "10:00",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropOpen,
          dropCloseTime: dropClose,
          dockOpenTimeInUTC: getDockTimeInUTC(safeDropDate, dropOpen),
          dockCloseTimeInUTC: getDockTimeInUTC(safeDropDate, dropClose),
          firstComeFirstServe: !!fields.DropFCFS,
        },
      },

      ltlQuoteDetail: {
        request: {
          productType: "LTL",
          quoteDetail: {
            customerId: assCustomerRowData?.id
              ? assCustomerRowData?.id
              : customerRowData?.id,
          },
          shipment: {
            shipmentDate:
              fields.PickUpDockDate !== "" && fields.PickUpDockTime !== ""
                ? getISODate(fields.PickUpDockDate, fields.PickUpDockTime)
                : fields.PickUpDockDate !== ""
                  ? getISODate(fields.PickUpDockDate, moment().format("hh:mm A"))
                  : "",

            originAddress: {
              address: {
                addressLineList: addrLineList(
                  fields.OriginAddress,
                  fields.OriginAPTSuite
                ),
                locality: fields.OriginCity || "",
                region: fields.OriginState || "",
                postalCode: fields.OriginZipcode || "",
                countryCode: fields.OriginCountry || "",
                companyName: fields.OriginBusinessLocation || "",
                phone: fields.OriginConsigneePhoneNumber
                  ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                  : "",
                contactList: [
                  {
                    phone: fields.OriginConsigneePhoneNumber
                      ? `${fields.OriginConsigneePhoneNumber.replace(
                        /\D/g,
                        ""
                      )}`
                      : "",
                    firstName: fields.OriginConsigneeName || "",
                  },
                ],
                locationType:
                  (pickupLimitedAccessSelected &&
                    pickupLimitedAccessSelected?.name) ||
                  "",
              },
            },

            destinationAddress: {
              address: {
                addressLineList: addrLineList(
                  fields.destinationAddress,
                  fields.destinationAPTSuite
                ),
                locality: fields.destinationCity || "",
                region: fields.destinationState || "",
                postalCode: fields.destinationZipcode || "",
                countryCode: fields.destinationCountry || "",
                companyName: fields.destinationBusinessLocation || "",
                phone: fields.destinationConsigneePhoneNumber
                  ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                  : "",
                contactList: [
                  {
                    phone: fields.destinationConsigneePhoneNumber
                      ? fields.destinationConsigneePhoneNumber.replace(
                        /\D/g,
                        ""
                      )
                      : "",
                    firstName: fields.destinationConsigneeName || "",
                  },
                ],
                locationType:
                  (dropLimitedAccessSelected &&
                    dropLimitedAccessSelected?.name) ||
                  "",
              },
            },

            // NOTE: mapLTLItems must itself ensure:
            // - top-level packagingType is STRING
            // - shippedItemList.packagingType is OBJECT {id,name}
            // - linearFeet is STRING
            handlingUnitList, // ← enforced via post-process above

            totalWeight: {
              value: shipmentTotalWeight, // consistent sum
              unit: "LB",
            },

            insuranceRequestFlag: false,
            totalHandlingUnitCount: Number(fields.destinationHandingUnits || 0),
            shipmentReferenceList: fields.orderReference
              ? mapReferences(true)
              : [],

            appointmentDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Appointment"
            ),
            directDeliveryOnlyFlag: null,
            holdAtTerminalFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Hold"
            ),
            insideDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Inside Delivery"
            ),
            insidePickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Inside Pickup"
            ),
            carrierTerminalPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Airport Pickup"
            ),
            liftgateDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "LiftgateDelivery"
            ),
            liftgatePickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Liftgate"
            ),
            notifyBeforeDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Delivery Notification"
            ),
            protectionFromColdFlag: false,
            residentialDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Residential"
            ),
            residentialPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Residential"
            ),
            sortAndSegregateFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Sort and Segregate"
            ),

            pickupSpecialInstructions: fields.PickUpDOckNote || "",
            deliverySpecialInstructions: fields.DropDOckNote || "",

            tradeshowDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Tradeshow Delivery"
            ),
            palletJackDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Pallet Jack"
            ),
            whiteGloveNoAssemblyDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "White Glove No Assembly"
            ),
            whiteGloveWithAssemblyDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "White Glove with Assembly"
            ),
            tradeshowDeliveryName: "",
            tradeshowPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Tradeshow Pickup"
            ),
            tradeshowPickupName: "",
          },
        },
        correlationId: "WWEX-M2M-shopFlow-ltl",
      },
    };

    try {
      setLoading(true);
      const apiResponse = await postApi<any>(
        `/order/order-quote/traditionalquote`,
        body
      );

      if (apiResponse.success) {
        const resp = apiResponse?.data || {};
        SetQuoteData(resp?.traditonalQuote);
        setShowLTLList(true);
        setIsLTLFinalMile(false);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const convertTo24HourFormat = (time: string): string => {
    return moment(time, "h:mm A").format("HH:mm");
  };

  /**
   * Prevents invalid characters from being entered in numeric fields.
   *
   * @param {KeyboardEvent<HTMLInputElement>} e - The keyboard event for the input
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  /**
   * handleAddItemDetails
   *
   * Adds a new row to the item details table.
   */
  const handleAddItemDetails = () => {
    const newRow = {
      id: "",
      serial: data.length + 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      weightUnit: { id: "lbs", name: "LBS" },
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: { id: "IN", name: "IN" },
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      linearFeet: "",
      package: [],
      stackable: { id: "No", name: "No" },
      hazmat: false,
    };
    setData([newRow, ...data]);
  };
  /**
   * handleBack
   *
   * Handles the back navigation logic, checking if there are unsaved changes.
   */
  const handleBack = () => {
    if (isFormChanged) {
      handleNavigation();
    } else {
      navigate(-1);
    }
  };

  const handlePhoneBlur = async (
    phoneNumber: string,
    phoneExt: string,
    fieldName: string
  ): Promise<boolean> => {
    const phoneMessages: Record<string, { required: string; valid: string }> = {
      OriginConsigneePhoneNumber: {
        required: "Phone number is required.",
        valid: "Please enter a valid phone number.",
      },
      destinationConsigneePhoneNumber: {
        required: "Consignee phone number is required.",
        valid: "Please enter a valid consignee phone number.",
      },
    };
    const messages = phoneMessages[fieldName] ?? {
      required: "Phone number is required.",
      valid: "Please enter a valid phone number.",
    };
    const rawPhone = phoneNumber.replace(/\D/g, "");
    if (!rawPhone) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: messages.required,
      }));
      return false;
    }
    const countryData = (countryCode as any[])?.find(
      (item: any) => item.countryCallingCode === phoneExt
    );
    const countryShortName = countryData?.shortName || "US";
    const e164Phone = `${phoneExt}${rawPhone}`;
    const response = await getApi<any>(
      `${PHONE_VALIDATE_API}?phoneNumber=${encodeURIComponent(e164Phone)}&countryCode=${countryShortName}`
    );
    if (!response.success || !response.data?.isValid) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: messages.valid,
      }));
      return false;
    } else {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
      return true;
    }
  };

  /**
   * handleFieldChange
   *
   * Handles changes to form input fields, updating the state and checking for errors.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event object from the input field.
   */
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "destinationConsigneeName" || name === "OriginConsigneeName") {
      // Allow only alphabetic characters and special characters, no numeric values
      updatedValue = value.replace(/[0-9]/g, "");
    }
    if (
      name === "destinationZipcode" ||
      name === "OriginZipcode" ||
      name === "PickUpDockDate" ||
      name === "PickUpDockTime"
    ) {
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }

    // Prevent leading whitespace
    updatedValue = updatedValue.trimStart();

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    checkInputChange();
    compareExpediteDates({ ...fields, [name]: updatedValue });
    // check error
    let error = "";
    switch (name) {
      case "destinationConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(value, fields.destinationConsigneePhoneExt);
        break;
      case "OriginConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(value, fields.OriginConsigneePhoneExt);
        break;
      case "destinationZipcode":
        error = validateZipCode(value);
        break;
      case "OriginZipcode":
        error = validateZipCode(value);
        break;
      case "PickUpDockDate":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockTime: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.PickUpDockTime != "" &&
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,
            "Pickup Date & Time should be earlier than Drop date & Time",
            `${value} ${fields.PickUpDockTime}:00`
          );
        }

        break;
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${value}:00`
          );
        }
        break;
      case "DropDockDate":
        let time = fields.DropDockTime != "" ? fields.DropDockTime : "00:00";
        setErrors((prevErrors) => ({
          ...prevErrors,
          DropDockTime: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
        }));
        if (fields.PickUpDockDate === "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time cannnot be earlier than Current Date &Time"
          );
        } else if (fields.PickUpDockDate != "" && fields.PickUpDockTime != "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time should be greater than or equal to the pickup Date & Time",
            `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
            );
          }
        }

        break;
      case "destinationConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      case "OriginConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      default:
        break;
    }
    if (
      !(
        name === "PickupTargetDateFrom" ||
        name === "PickupTargetTimeFrom" ||
        name === "PickupTargetDateTo" ||
        name === "PickupTargetTimeTo" ||
        name === "PickupDeliveryDateFrom" ||
        name === "PickupDeliveryTimeFrom" ||
        name === "PickupDeliveryDateTo" ||
        name === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [name]: error,
        };
      });
    }
  };

  const compareExpediteDates = (fieldsVal: any) => {
    if (fieldsVal.PickupTargetDateFrom !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateFrom: "",
      }));
      if (fieldsVal.PickupTargetTimeFrom !== "") {
      }
      if (fieldsVal.PickupTargetDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Target Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
    }
    if (fieldsVal.PickupTargetDateTo !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateTo: "",
      }));
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeTo !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeTo
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
    }

    if (fieldsVal.PickupDeliveryDateFrom !== "") {
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Delivery Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`
        );

        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        } else {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: "",
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupDeliveryTimeFrom
          )}:00`,
          "Pickup delivery date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        }
      }
    }
    if (
      fieldsVal.PickupDeliveryDateTo !== "" &&
      fieldsVal.PickupDeliveryTimeTo !== ""
    ) {
      let error = ValidateExpedietedCrrentTime(
        `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(
          fieldsVal.PickupDeliveryTimeTo
        )}:00`,
        "Pickup delivery date to and time should be greater than current date and time"
      );
      if (error != "") {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: error,
        }));
      } else {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: "",
        }));
      }
    }
  };

  /**
   * handleTextAreaChange - Updates the textarea field values based on user input.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The textarea change event.
   */
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Prevent leading whitespace
    const trimmedValue = value.trimStart();

    checkInputChange();
    setFields((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));
  };

  /**
   * mapItemsDataToPayload - Maps the item details from the form data into a format
   * suitable for the API payload.
   *
   * @param {DataTypes[]} data - The array of item details entered by the user.
   * @returns The mapped array of item details for the API payload.
   */
  const mapItemsDataToPayload = (data: any[]) => {
    return data.map((item) => ({
      id: item?.descriptionId || "",
      description: item.description || "",
      handlingUnit: item.handlingUnit || "",
      quantity: item.quantity || "",
      weight: item.weight || "",
      weightUnit: item?.weightUnit?.id || "lbs",
      length: item.itemDimension?.length || "",
      width: item.itemDimension?.width || "",
      height: item.itemDimension?.height || "",
      unit: item.unit.id,
      itemClass: item.itemClass.toString() || "",
      comments: item.comments || "",
      nmfc: item.nmfc || "",
      skuNumber: item.skuNumber || "",
      packageType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      package: item.package
        .filter((x: any) => x.quantity !== "" && x.weight !== "")
        .map((x: any) => ({ ...x, id: x.descriptionId || "" })),
      stackable:
        item?.stackable == "Yes" ||
          item?.stackable?.name == "Yes" ||
          item?.stackable?.name == "yes"
          ? true
          : false || false,
      linearFeetStackable: 0,
      linearFeet: item?.linearFeet,
      density: item?.density,
      cbm: item?.cbm,
      hazardMaterial: item.hazmat
        ? {
          itemName: item.hazardMaterial?.itemName || "",
          packageType:
            typeof item.hazardMaterial?.packageType === "object"
              ? item.hazardMaterial?.packageType?.name
              : item.hazardMaterial?.packageType || "",
          hazmatClass: item.hazardMaterial?.hazmatClass || "",
          unno: item.hazardMaterial?.unno || "",
          properShippingName: item.hazardMaterial?.properShippingName || "",
          packagingGroup: item.hazardMaterial?.packagingGroup || "",
          contact: item.hazardMaterial?.contact || "",
          phoneNumber: item.hazardMaterial?.phoneNumber || "",
          countryCallingCode: item.hazardMaterial?.countryCallingCode || "",
          poison: item.hazardMaterial?.poison ? true : false,
          isReportableQuantity: item.hazardMaterial?.isReportableQuantity
            ? true
            : false,
          isMarinePollutant: item.hazardMaterial?.isMarinePollutant
            ? true
            : false,
        }
        : {},
    }));
  };

  /**
   * handleSave - Validates the form fields and saves the customer order by sending
   * the data to the API. If there are errors, it prevents submission.
   */
  const handleSave = async () => {
    if (!runValidation("saveOrder")) return;
    const nonPhoneErrors = Object.entries(errors).filter(
      ([key, value]) =>
        key !== "destinationConsigneePhoneNumber" &&
        key !== "OriginConsigneePhoneNumber" &&
        // runValidation just re-derived these; `errors` here is the stale render closure.
        !isMandatoryMessage(value)
    );
    if (nonPhoneErrors.some(([, value]) => !!value)) {
      return;
    }

    // All TraditionalOrder types (Expedited, LTL, LTL+FM) use both origin and destination phone
    const phoneResults = await Promise.all([
      handlePhoneBlur(fields.destinationConsigneePhoneNumber, fields.destinationConsigneePhoneExt, "destinationConsigneePhoneNumber"),
      handlePhoneBlur(fields.OriginConsigneePhoneNumber, fields.OriginConsigneePhoneExt, "OriginConsigneePhoneNumber"),
    ]);
    if (phoneResults.some((valid) => !valid)) {
      if (!phoneResults[1]) originPhoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      else destinationPhoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const isInternalLTL = state?.selectedData?.internalLTL;
    if (!notLTL && !isInternalLTL) {
      if (orderTypeRowData?.name === "Expedited") {
        handleSaveExpediet();
      } else if (orderTypeRowData?.name === "LTL") {
        handleSaveLTL();
      } else {
        handleSaveLTLFinalMile();
      }
      return;
    }
    const apiPayload = state?.selectedData?.internalLTL
      ? {
        quoteFromScreen: "order",
        groupId: groupQuoteId,
        banyanType: state?.selectedData?.banyanType,
        providenceType: state?.selectedData?.providenceType,
        associatedCustomerId: assCustomerRowData?.id,
        customerId: customerRowData?.id || "",
        orderTypeId: orderTypeRowData?.id || "",
        reference: fields.orderReference || "",
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        // LTL Carrier Details captured on the quote panel — internal LTL orders
        // carry them alongside the references, not under a separate LTL block.
        threePLProvider: fields.plProvider || "",
        courierProOne: fields.courierPro1 || "",
        serviceId: state?.selectedData?.serviceId || "",
        items: mapItemsDataToPayload(data),
        agentHubId: state?.selectedData?.agentHubId || "",
        quoteId: state?.selectedData?.quoteId || "",
        allQuoteIds: state?.selectedData?.quoteId?.length
          ? [state?.selectedData?.quoteId]
          : [],
        originAddress: {
          address: fields.OriginAddress || "",
          aptSuit: fields.OriginAPTSuite || "",
          id: addressSelectedOrigin?.id || "",
          zipCode: fields.OriginZipcode || "",
          city: fields.OriginCity || "",
          state: fields.OriginState || "",
          country: fields.OriginCountry || "",
          businessLocation: fields.OriginBusinessLocation || "",
          contactName: fields.OriginConsigneeName || "",
          phoneNumber:
            fields.OriginConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.OriginConsigneePhoneExt || "+1",
          contactEmail: fields.OriginConsigneeContactEmail || "",
          accessorials: pickupAccessorialsSelected || [],
          locationType: pickupLimitedAccessSelected?.name || "",
        },
        destinationAddress: {
          id: addressSelected?.id || "",
          address: fields.destinationAddress || "",
          aptSuit: fields.destinationAPTSuite || "",
          zipCode: fields.destinationZipcode || "",
          city: fields.destinationCity || "",
          state: fields.destinationState || "",
          country: fields.destinationCountry || "",
          businessLocation: fields.destinationBusinessLocation || "",
          contactName: fields.destinationConsigneeName || "",
          phoneNumber:
            fields.destinationConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.destinationConsigneePhoneExt || "+1",
          contactEmail: fields.destinationConsigneeContactEmail || "",
          extraMen: fields.destinationExtramen || "",
          waitTime: fields.destinationWaitTimeMinutes || "",
          stairsNumber: fields.destinationNoOfFlights || "",
          storage: fields.destinationStorage || "",
          handlingUnit: fields.destinationHandingUnits || 0,
          weight: fields.destinationWeight || 0,
          pieces: fields.destinationPieces || 0,
          specialInstructions: fields.destinationSpecialInstructions || "",
          accessorials: dropAccessorialsSelected || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
          pickUpTime: fields.PickUpDockTime || "",
          note: fields.PickUpDOckNote || "",
          dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
          dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
          firstComeFirstServe: fields.PickUpFCFS || false,
        },
        dropDock: {
          dockName: fields.DropDockName || "",
          pickupNumber: fields.DropDOckNumber || "",
          dropDate: fields.DropDockDate || fields.PickUpDockDate || "",
          dropTime: fields.DropDockTime || "",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
          dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
          firstComeFirstServe: fields.DropFCFS || false,
        },
        document: {
          urls: documentInfo.urls || [],
        },
        orderSwapAndExchange: fields.additionalServices?.includes("Order Swap & Exchange") || false,
        additionalServices: fields.additionalServices || [],
        removeAndDisposal: fields.additionalServices?.includes("Remove & Disposal") || false,
      }
      : {
        quoteFromScreen: "order",
        groupId: groupQuoteId,
        customerId: customerRowData?.id,
        associatedCustomerId: assCustomerRowData?.id,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        serviceId: serviceSelected?.id,
        items: mapItemsDataToPayload(data),
        agentHubId: fields?.agentHubId,
        // selected quote id
        quoteId: fields.selectedQuoteId,
        // all quote id's
        allQuoteIds: fields.allQuoteId,
        destinationAddress: {
          address: fields.destinationAddress,
          id: addressSelected?.id || "",
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
        },
        midStop: {
          address: fields.MidStopAddress,
          aptSuit: fields.MidStopAPTSuit,
          zipCode: fields.MidStopZipcode,
          city: fields.MidStopCity,
          state: fields.MidStopState,
          country: fields.MidStopCountry,
          contactName: fields.MidStopContactName,
          contactEmail: fields.MidStopContactEmail,
          phoneNumber: fields.MidStopPhoneNumber,
          countryCallingCode: fields.MidStopPhoneNumberExt,
        },
        document: {
          fileUrl: documentInfo.files[0]?.fileUrl,
          urls: documentInfo.urls,
        },
        orderSwapAndExchange: fields.additionalServices.includes("Order Swap & Exchange"),
        additionalServices: fields.additionalServices,
        removeAndDisposal: fields.additionalServices.includes("Remove & Disposal"),
      };
    try {
      setLoading(true);
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(`/order`, apiPayload);
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(`/order`, apiPayload);
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExpediet = async () => {
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      agentId: agentRowData?.id,
      agentHubId: agentHubRowData?.id,
      serviceId: serviceSelected?.id,
      reference: fields.orderReference,
      reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
      reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
      reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
      reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
      courierProOne: fields.courierPro1,
      courierProTwo: fields.courierPro2,
      originCity: fields.originCity,
      distanceToDestination: "",
      totalPrice: fields.serviceCost,
      items: mapItemsDataToPayload(data),
      originAddress: {
        address: fields.OriginAddress || "",
        aptSuit: fields.OriginAPTSuite || "",
        zipCode: fields.OriginZipcode || "",
        city: fields.OriginCity || "",
        state: fields.OriginState || "",
        country: fields.OriginCountry || "",
        businessLocation: fields.OriginBusinessLocation || "",
        contactName: fields.OriginConsigneeName || "",
        phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.OriginConsigneePhoneExt || "",
        contactEmail: fields.OriginConsigneeContactEmail || "",
        pickUpTargetDateTimeFrom: getISODate(
          fields?.PickupTargetDateFrom,
          fields?.PickupTargetTimeFrom
        ),
        pickUpTargetDateTimeTo: getISODate(
          fields?.PickupTargetDateTo,
          fields?.PickupTargetTimeTo
        ),
        pickUpDeliveryTargetDateTimeFrom: getISODate(
          fields?.PickupDeliveryDateFrom,
          fields?.PickupDeliveryTimeFrom
        ),
        pickUpDeliveryTargetDateTimeTo: getISODate(
          fields?.PickupDeliveryDateTo,
          fields?.PickupDeliveryTimeTo
        ),
      },
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      document: {
        fileUrl: documentInfo.files[0]?.fileUrl,
        urls: documentInfo.urls,
      },
    };
    try {
      setLoading(true);

      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(
            `/order/createexpeditedorder`,
            apiPayload
          );
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(
          `/order/createexpeditedorder`,
          apiPayload
        );
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  function fixFormat(dateStr: string): string {
    // If it's already in 'YYYY-MM-DD' format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // If it's an ISO string (like '2025-07-01T19:00:00.000Z'), extract date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0]; // Extracts 'YYYY-MM-DD'
    }

    // If invalid date, return empty string to avoid sending bad payload
    return "";
  }


  const handleSaveLTL = async () => {

    let apiPayload = {
      groupId: groupQuoteId,
      request: {
        quoteDetail: {
          banyanType: state?.selectedData?.banyanType,
          providenceType: state?.selectedData?.providenceType,
          loadId: fields?.loadId,
          orderFor: fields?.orderFor,
          source: fields?.source,
          serviceId: serviceSelected?.id,
          customerId: customerRowData?.id,
          associatedCustomerId: assCustomerRowData?.id,
          orderTypeId: orderTypeRowData?.id,
          isLtlOnly: isLtlOnly,
          scac: fields?.scac || quickQuoteLtlData?.scac || "",
          reference: fields.orderReference || "",
          reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
          reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
          reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
          reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
          distanceToDestination:
            fields.distance || quickQuoteLtlData?.miles || "",
          totalWeight: fields.destinationWeight || "",
          threePLProvider: fields.plProvider || "",
          courierProOne: fields.courierPro1 || "",
          courierProTwo: fields.offerId || quickQuoteLtlData?.offerId || "",
          items: mapItemsDataToPayload(data),
          pickUpDock: {
            dockName: fields.PickUpDockName || "",
            pickupNumber: fields.PickUpDOckNumber || "",
            pickUpDate: getISODate(
              fields.PickUpDockDate || "",
              fields.PickUpDockTime || ""
            ),
            pickUpTime: fields.PickUpDockTime || "",
            note: fields.PickUpDOckNote || "",
            dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
            dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
            firstComeFirstServe: fields.PickUpFCFS,
          },

          dropDock: {
            dockName: fields.DropDockName || "",
            pickupNumber: fields.DropDOckNumber || "",
            dropDate: "2024-12-12",
            dropTime: "19:44",
            note: fields.DropDOckNote || "",
            dropOpenTime: convertTo24HourFormat(fields.DropDockOpenTime) || "",
            dropCloseTime:
              convertTo24HourFormat(fields.DropDOckCloseTime) || "",
            firstComeFirstServe: fields.DropFCFS,
          },
          originAddress: {
            id: fields?.OriginAddressId || "",
            address: fields.OriginAddress || "",
            aptSuit: fields.OriginAPTSuite || "",
            zipCode: fields.OriginZipcode || "",
            city: fields.OriginCity || "",
            state: fields.OriginState || "",
            country: fields.OriginCountry || "",
            businessLocation: fields.OriginBusinessLocation || "",
            contactName: fields.OriginConsigneeName || "",
            phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
            countryCallingCode: fields.OriginConsigneePhoneExt || "",
            contactEmail: fields.OriginConsigneeContactEmail || "",
            accessorials: pickupAccessorialsSelected,
            locationType: pickupLimitedAccessSelected?.name || "",
          },
          destinationAddress: {
            id: fields?.destinationAddressId || "",
            address: fields.destinationAddress || "",
            aptSuit: fields.destinationAPTSuite || "",
            zipCode: fields.destinationZipcode || "",
            city: fields.destinationCity || "",
            state: fields.destinationState || "",
            country: fields.destinationCountry || "",
            businessLocation: fields.destinationBusinessLocation || "",
            contactName: fields.destinationConsigneeName || "",
            phoneNumber: fields.destinationConsigneePhoneNumber.replace(
              /\D/g,
              ""
            ),
            countryCallingCode: fields.destinationConsigneePhoneExt || "",
            contactEmail: fields.destinationConsigneeContactEmail || "",
            handlingUnit: fields.destinationHandingUnits || "",
            weight: fields.destinationWeight || "",
            pieces: fields.destinationPieces || "",
            accessorials: dropAccessorialsSelected,
            locationType: dropLimitedAccessSelected?.name || "",
          },
        },
        shipmentProductTransactionId:
          fields?.orderFor === "banyan" || fields?.orderFor === "providence"
            ? fields?.quoteId
            : fields.productTransactionId ||
            quickQuoteLtlData?.productTransactionId ||
            "",
        shipmentOfferId:
          fields?.orderFor === "banyan" || fields?.orderFor === "providence"
            ? fields?.quoteId
            : fields.offerId || quickQuoteLtlData?.offerId || "",
        ...(fields?.orderFor === "providence" && { quoteDetailId: fields?.quoteId ?? "" }),
        shipment: {},
        isSelfScheduled: false,
        pickupDate: `${fixFormat(
          fields.PickUpDockDate
        )} ${convertTo24HourFormat(fields.PickUpDockTime)}:00`,
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "",
        shipmentReferenceList: mapReferences(true),
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };
    try {
      setLoading(true);

      const apiResponse = await postApi(`/order/createltlorder`, apiPayload);
      if (apiResponse.success) {
        handleUpdateNavigation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
        navigate("/all-orders");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLTLFinalMile = async () => {
    const fm = ltlFMData?.finalMileData;
    const fmService = fm?.service;
    const fmHub = ltlFMData?.hub || fm?.hub;
    const fmAddr = fmHub?.address;
    const fmCustomer = fmService?.customer;

    // Prefer form values; otherwise take from ltlFMData root
    const offerId = fields.offerId || ltlFMData?.offerId;
    const transactionId =
      fields.productTransactionId || ltlFMData?.productTransactionId;

    const fallbackStr = (...vals: Array<string | undefined | null>) =>
      (vals.find(
        (v) => typeof v === "string" && v.trim().length > 0
      ) as string) || "";

    const fallbackNum = (
      ...vals: Array<number | string | undefined | null>
    ) => {
      const n = vals.find((v) => {
        if (v === null || v === undefined) return false;
        const num = typeof v === "string" ? Number(v) : v;
        return typeof num === "number" && !Number.isNaN(num);
      });
      return Number(typeof n === "string" ? Number(n) : n) || 0;
    };

    const asEmailArray = (val?: string | string[] | null): string[] => {
      if (Array.isArray(val)) return val.filter(Boolean);
      if (typeof val === "string" && val.trim()) return [val.trim()];
      return [];
    };

    // ---------- SAFE TIME HELPERS (eliminate "Invalid date") ----------
    const isBad = (v?: string | null) =>
      !v ||
      typeof v !== "string" ||
      v.trim() === "" ||
      v.toLowerCase().includes("invalid");

    const safeConvertTo24Hour = (val?: string) => {
      const input = typeof val === "string" ? val : "";
      return typeof convertTo24HourFormat === "function"
        ? convertTo24HourFormat(input)
        : input;
      // if convertTo24HourFormat is missing, the UI should already be giving "HH:mm"
    };

    const coalesceTime = (
      input?: string,
      fallback?: string,
      defaultVal?: string
    ): string => {
      const t = safeConvertTo24Hour(input);
      if (!isBad(t)) return t!;
      const tfb = safeConvertTo24Hour(fallback);
      if (!isBad(tfb)) return tfb!;
      return defaultVal || "00:00";
    };

    const toIsoUTC = (dateTimeStr?: string): string | undefined => {
      if (!dateTimeStr || typeof dateTimeStr !== "string") return undefined;
      const s = dateTimeStr.replace(" ", "T");
      const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
      return isNaN(d.getTime()) ? undefined : d.toISOString();
    };

    const format12h = (dateTimeStr?: string): string | undefined => {
      if (!dateTimeStr || typeof dateTimeStr !== "string") return undefined;
      const s = dateTimeStr.replace(" ", "T");
      const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
      if (isNaN(d.getTime())) return undefined;
      const hh = d.getUTCHours();
      const mm = d.getUTCMinutes().toString().padStart(2, "0");
      const ampm = hh >= 12 ? "PM" : "AM";
      const h12 = ((hh + 11) % 12) + 1;
      return `${h12.toString().padStart(2, "0")}:${mm} ${ampm}`;
    };
    // -----------------------------------------------------------------

    // From offer: SCAC + total price (used as fallbacks)
    const scacFromOffer =
      fields.scac ||
      (ltlFMData as any)?.scac ||
      ltlFMData?.primaryVendor?.scac ||
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit?.scac ||
      "";

    const totalPriceFromOffer =
      typeof ltlFMData?.totalPrice === "number"
        ? ltlFMData.totalPrice
        : undefined;

    const customerMarkupTypeFromFM =
      fields.customerMarkupType ||
      fm?.rateDetails?.customerMarkupType ||
      (ltlFMData as any)?.customerMarkUpType ||
      "percent";

    const distanceToDestination = fallbackNum(
      Number(fields.finalMileDistance),
      fm?.distanceToDestination,
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
        ?.milesInTransit
    );

    // Prefer UI date/time; fallback to offer shipmentDate if UI invalid
    const shipmentDateFromOffer =
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.shipmentDate; // "YYYY-MM-DD HH:mm:ss"

    const uiISO = getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || "");

    const pickUpISODate = !isBad(uiISO)
      ? (uiISO as string)
      : toIsoUTC(shipmentDateFromOffer) || new Date().toISOString();

    const pickUpDisplayTime = fields.PickUpDockTime || format12h(shipmentDateFromOffer) || "08:00 AM";

    // pickup/delivery hours (now safe)
    const pickOpen = coalesceTime(
      fields.PickUpDockOpenTime,
      fmAddr?.operationHrsFrom,
      "00:00"
    );
    const pickClose = coalesceTime(
      fields.PickUpDOckCloseTime,
      fmAddr?.operationHrsTo,
      "23:59"
    );
    const dropOpen = coalesceTime(
      (fields as any).DropDockOpenTime,
      fmAddr?.operationHrsFrom,
      "00:00"
    );
    const dropClose = coalesceTime(
      (fields as any).DropDOckCloseTime,
      fmAddr?.operationHrsTo,
      "23:59"
    );

    // Delivery date: prefer ETA from LTL time-in-transit; else ensure not before pickup date
    const etaDate =
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
        ?.estimatedDeliveryDate; // "YYYY-MM-DD"
    const safeDropDate = (() => {
      if (etaDate && /^\d{4}-\d{2}-\d{2}$/.test(etaDate)) return etaDate;
      const pu = new Date(pickUpISODate);
      if (isNaN(pu.getTime())) return "2024-12-12"; // last-known default from your payloads
      // add a minimal buffer of 1 day if no ETA:
      const d = new Date(pu);
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().slice(0, 10);
    })();
    const apiPayload = {
      groupId: groupQuoteId || "",

      // ---------------- quoteDetail ----------------
      quoteDetail: {
        // banayType: state?.selectedData?.banyanType,
        customerNumber:
          (customerRowData as any)?.customerNumber ||
          (customerRowData as any)?.customer?.customerNumber ||
          fmCustomer?.customerNumber ||
          "",

        orderTypeName:
          (orderTypeRowData as any)?.name ||
          (fields as any)?.orderTypeName ||
          "LTL + Final Mile",

        agentHubName:
          (fields as any)?.AgentHub?.name ||
          (fields as any)?.agentHubName ||
          fmHub?.name ||
          quickQuoteLtlFMData?.hub?.name ||
          "",

        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",

        // 👉 REQUIRED: finalmile_quote_id must be service.quoteId
        serviceId:
          serviceSelected?.id ||
          fmService?.id ||
          quickQuoteLtlFMData?.service?.id ||
          (ltlFMData as any)?.serviceId ||
          "",
        finalmile_quote_id: fmService?.quoteId || "",

        distanceToDestination,
        totalWeight: fallbackNum(
          Number(fields.destinationWeight),
          fm?.totalWeight
        ),

        // Prefer offer total price else FM quote price
        totalPrice:
          typeof totalPriceFromOffer === "number"
            ? totalPriceFromOffer
            : typeof fm?.quotePrice === "number"
              ? fm.quotePrice
              : 0,

        customerMarkupType: customerMarkupTypeFromFM,

        // FM quoteId first; include LTL offerId; filter out empties
        allQuoteIds: [
          fmService?.quoteId || fields.selectedQuoteId || "",
          offerId || fields.offerId || "",
        ].filter(Boolean),

        destinationAddress: {
          id: fields?.destinationAddressId || "",
          address: fields.destinationAddress || "",
          aptSuit: fields.destinationAPTSuite || "",
          zipCode: fields.destinationZipcode || "",
          city: fields.destinationCity || "",
          state: fields.destinationState || "",
          country: fields.destinationCountry || "",
          businessLocation: fields.destinationBusinessLocation || "",
          contactName: fields.destinationConsigneeName || "",
          phoneNumber:
            fields.destinationConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.destinationConsigneePhoneExt || "",
          contactEmail: fields.destinationConsigneeContactEmail || "",
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits || "",
          weight: fields.destinationWeight || "",
          pieces: fields.destinationPieces || "",
          accessorials: mapAccessorials(dropAccessorialsSelected) || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },

        midStop: {
          address:
            fields.MidStopAddress ||
            fmAddr?.address ||
            quickQuoteLtlFMData?.hub?.address?.address ||
            "",
          aptSuit:
            fields.MidStopAPTSuit ||
            fmAddr?.aptSuit ||
            quickQuoteLtlFMData?.hub?.address?.aptSuit ||
            "",
          zipCode:
            fields.MidStopZipcode ||
            fmAddr?.zipCode ||
            quickQuoteLtlFMData?.hub?.address?.zipCode ||
            "",
          city:
            fields.MidStopCity ||
            fmAddr?.city ||
            quickQuoteLtlFMData?.hub?.address?.city ||
            "",
          state:
            fields.MidStopState ||
            fmAddr?.state ||
            quickQuoteLtlFMData?.hub?.address?.state ||
            "",
          country:
            fields.MidStopCountry ||
            fmAddr?.country ||
            quickQuoteLtlFMData?.hub?.address?.country ||
            "",
          contactName:
            fields.MidStopContactName ||
            fmHub?.agent?.name ||
            fmHub?.name ||
            quickQuoteLtlFMData?.hub?.address?.name ||
            "",
          contactEmail: asEmailArray(
            fields.MidStopContactEmail ||
            fmHub?.operationEmail ||
            quickQuoteLtlFMData?.hub?.address?.operationEmail
          ),
          phoneNumber:
            fields.MidStopPhoneNumber ||
            fmAddr?.phoneNumber ||
            quickQuoteLtlFMData?.hub?.address?.phoneNumber ||
            "",
          countryCallingCode:
            fields.MidStopPhoneNumberExt ||
            fmAddr?.countryCallingCode ||
            quickQuoteLtlFMData?.hub?.address?.countryCallingCode ||
            "+1",
        },

        document: { fileUrl: "", urls: documentInfo.urls },
        additionalServices: [],
        items: mapItemsDataToPayload(data), // keeps your item IDs passthrough intact
      },

      // ---------------- ltlQuoteDetail ----------------
      ltlQuoteDetail: {
        threePLProvider: fields.plProvider || "",
        courierProOne: fields.courierPro1 || "",
        scac: scacFromOffer,
        agentName: !scacFromOffer && (fields?.internalLTL || state?.selectedData?.internalLTL) ? (fmHub?.agent?.name || fields?.CarrierName || "") : "",
        courierProtwo: fields.courierPro2 || "",
        customerMarkupType: customerMarkupTypeFromFM,

        shipmentProductTransactionId: transactionId || "",
        shipmentOfferId: offerId || "",

        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: pickUpISODate,
          pickUpTime: pickUpDisplayTime,
          note: fields.PickUpDOckNote || "",
          dockOpenTime: pickOpen,
          dockCloseTime: pickClose,
          firstComeFirstServe: !!fields.PickUpFCFS,
        },

        dropDock: {
          dockName: (selectedHubData as any)?.hubName || fmHub?.name || "",
          pickupNumber: fields.DropDOckNumber || "",
          // ensure drop is not before pickup; prefer ETA if available
          dropDate: safeDropDate,
          dropTime: "10:00",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropOpen,
          dropCloseTime: dropClose,
          firstComeFirstServe: !!fields.DropFCFS,
        },

        originAddress: {
          id: fields?.OriginAddressId || "",
          address: fields.OriginAddress || "",
          aptSuit: fields.OriginAPTSuite || "",
          zipCode: fields.OriginZipcode || "",
          city: fields.OriginCity || "",
          state: fields.OriginState || "",
          country: fields.OriginCountry || "",
          businessLocation: fields.OriginBusinessLocation || "",
          contactName: fields.OriginConsigneeName || "",
          phoneNumber:
            fields.OriginConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.OriginConsigneePhoneExt || "",
          contactEmail: fields.OriginConsigneeContactEmail || "",
          accessorials: mapAccessorials(pickupAccessorialsSelected) || [],
          locationType: pickupLimitedAccessSelected?.name || "",
        },

        shipment: {},
        isSelfScheduled: false,

        pickupDate: `${fixFormat(
          fields.PickUpDockDate
        )} ${convertTo24HourFormat(fields.PickUpDockTime)}:00`,
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "23:59:00",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "00:00:00",

        shipmentReferenceList: fields.orderReference ? mapReferences(true) : [],
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };

    try {
      setLoading(true);

      // Upload doc if present
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );

        if (!uploadApiResponse.success) {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
          setLoading(false);
          return;
        }
        apiPayload.quoteDetail.document.fileUrl =
          uploadApiResponse?.data?.url || "";
      }

      const apiResponse = await postApi(
        `/order/ltl-plus-finalmile`,
        apiPayload
      );
      if (apiResponse.success) {
        handleUpdateNavigation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
        navigate("/all-orders");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const mapAccessorials = (accessorialsData: any[]): any[] => {
    return accessorialsData.map((item) => ({
      id: item?.id || "",
      name: item?.name || "",
    }));
  };

  /**
   * getZipcodeDetails - Fetches and updates city, state, and country information
   * based on the provided zipcode.
   */
  const getZipcodeDetails = async () => {
    if ((fields.destinationZipcode?.length ?? 0) < 5) {
      setDestinationCities([]);
      setFields((prev) => ({ ...prev, destinationCity: "", destinationState: "", destinationCountry: "" }));
    }
    if (fields.destinationZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.destinationZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setDestinationCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          destinationCity: primaryCity,
          destinationState: data?.state || "",
          destinationCountry: data?.country || "",
        }));
      }
    }
    if ((fields.OriginZipcode?.length ?? 0) < 5) {
      setOriginCities([]);
      setFields((prev) => ({ ...prev, OriginCity: "", OriginState: "", OriginCountry: "" }));
    }
    if (fields.OriginZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.OriginZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setOriginCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          OriginCity: primaryCity,
          OriginState: data?.state || "",
          OriginCountry: data?.country || "",
        }));
      }
    }
  };

  /**
   * getQuotesData - Fetches quotes for the entered data, and updates the state
   * with the returned quotes. If the quotes are successfully fetched, it maps the
   * data into a usable format for the application.
   */
  const getQuotesData = async () => {
    if (!runValidation("getQuote")) return;
    resetLocationState();
    setGetQuote(true);
    setStateQuoteId("");
    // Clear the whole previous quote before the request goes out, so nothing from the
    // last click can survive into this one; the response below repopulates it and the
    // loader covers the gap. This is the only place that reliably runs on every click:
    // `getQuote` is already true on a repeat click, so setGetQuote(true) is a no-op and
    // the reset effect keyed on it never fires again.
    SetQuoteData([]);
    setHasMasterHub(false);
    setMasterHubName("");
    setMasterHubAddress({});
    // empty mid stop fields value
    setFields((prev: any) => ({
      ...prev,
      MidStopAddress: "",
      MidStopAPTSuit: "",
      MidStopZipcode: "",
      MidStopCity: "",
      MidStopState: "",
      MidStopCountry: "",
      MidStopContactName: "",
      MidStopPhoneNumber: "",
      MidStopPhoneNumberExt: "",
      MidStopContactEmail: [],
    }));
    // set api payload data
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      serviceIds: [serviceSelected?.id],
      items: mapItemsDataToPayload(data),
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      additionalServices: fields.additionalServices,
    };
    try {
      setLoading(true);
      const apiResponse = await postApi<GetQuoteApiResponse[]>(
        `/order/order-quote/generate`,
        apiPayload
      );
      if (apiResponse.success) {
        const data = apiResponse?.data || [];

        const mappedData = data.map((item) => {
          // Read this item's own master hub. The setters below do not update
          // hasMasterHub/masterHubName/masterHubAddress in this closure, so the rows
          // were built from whatever the previous quote left behind — no master hub at
          // all on the first call, and the previous call's on every one after it.
          // `masterHub` can also come back empty even with a `masterHubId` set, so read
          // it defensively: a throw here escapes to the catch below with the list intact.
          const itemHasMasterHub = !!item.hub?.masterHubId;
          const itemMasterHubName = item.masterHub?.name || "";
          const itemMasterHubAddress = item.masterHub?.address;
          if (itemHasMasterHub) {
            setHasMasterHub(true);
            setMasterHubName(itemMasterHubName);
            // Never store undefined: the `getQuoteBox` below reads masterHubAddress.city
            // unguarded once hasMasterHub is true, so an id without a populated
            // masterHub would take the render down.
            setMasterHubAddress(itemMasterHubAddress || {});
          }
          // Collect all quoteIds for the current item
          const allQuoteIds = item.services
            .map((service) => service?.quoteId)
            .filter((quoteId) => quoteId != null);

          const services = item.services.map((service, serviceIndex) => ({
            id: service?.id,
            serial: serviceIndex + 1,
            serviceName: service?.name || "",
            description: service?.description || "",
            serviceLevel: service?.serviceType?.name,
            quotePrice: service?.quotePrice || 0.0,
            fuelSurcharge: service?.rateDetails?.fuelSurcharge || 0.0,
            additionalOrderCharges:
              service?.rateDetails?.additionalOtherCharges,
            basePrice: service?.rateDetails?.basePrice || 0.0,
            totalWeight: service?.totalWeight || 0,
            deliverWithin: service?.deliverWithin || 0,
            distanceToDestination: service?.distanceToDestination || 0,
            shortName: service?.shortName || "",
            longName: service?.longName || "",
            useOver150Lbs: service?.useOver150Lbs || false,
            address: itemHasMasterHub ? itemMasterHubAddress : item.hub.address,
            operationHrsFrom: itemHasMasterHub
              ? itemMasterHubAddress?.operationHrsFrom
              : item.hub?.address?.operationHrsFrom || "",
            operationHrsTo: itemHasMasterHub
              ? itemMasterHubAddress?.operationHrsTo
              : item.hub?.address?.operationHrsTo || "",
            hubName: itemHasMasterHub ? itemMasterHubName : item.hub?.name || "",
            agentHubId: item.hub?.id || "",
            addressName: itemHasMasterHub
              ? itemMasterHubAddress?.address
              : item.hub?.address?.address || "",
            agentName: item.hub?.agent?.name || "",
            quoteId: service?.quoteId,
            allQuoteId: allQuoteIds,
          }));
          const info: QuoteInfo = {
            agentName: item.hub?.agent?.name || "",
            operationHrsFrom: item.hub?.address?.operationHrsFrom || "",
            operationHrsTo: item.hub?.address?.operationHrsTo || "",
            hubName: item.hub?.name || "",
            addressName: item.hub?.address?.address || "",
            address: item.hub.address,
            distanceToDestination:
              item.services?.[0]?.distanceToDestination || 0,
          };

          return { info, services };
        });
        SetQuoteData(mappedData);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      // Anything thrown while mapping escapes here with quoteData untouched, which
      // leaves the previous quote's agents on screen as though they were this
      // request's result. Clear the list and say so rather than showing stale hubs.
      SetQuoteData([]);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  /** Red outline for any field currently carrying a validation message. */
  const fieldErrorClass = (key: string): string =>
    errors?.[key] ? " form__input--error" : "";

  /**
   * Same, for controls wrapped in `form__groupField` (the phone number's country-code
   * group). The wrapper draws the border there, so the input's own modifier is invisible.
   */
  const groupFieldErrorClass = (key: string): string =>
    errors?.[key] ? " form__groupField--error" : "";

  /**
   * Clear handling-unit highlights as soon as the values arrive — typed in, or filled in
   * bulk by picking a saved item from the Description dropdown.
   */
  useEffect(() => {
    setInvalidCells((prev) => pruneItemIssues(prev, data));
    setInvalidPackageCells((prev) => prunePackageIssues(prev, data));
    if (data?.length > 0) setItemsEmpty(false);
  }, [data]);

  /**
   * runValidation - Checks everything the Get Quote and Save buttons used to check
   * silently through their `disabled` expressions, and turns any gap into visible
   * feedback: a message under each blank field plus scroll-and-focus on the first one.
   *
   * @returns true when the caller may proceed.
   */
  const runValidation = (
    intent: "saveOrder" | "getQuote" | "getLtlQuote"
  ): boolean => {
    const result = validateOrderForm({
      intent,
      fields,
      data,
      errors,
      orderTypeName: orderTypeRowData?.name,
      notLTL,
      customerId: customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      serviceId: serviceSelected?.id,
      agent: agentRowData,
      agentHub: agentHubRowData,
      // This screen fixes the order type to LTL on mount and shows no Service Level
      // picker, so neither id is something the user could supply.
      requireOrderTypeId: false,
      requireServiceId: false,
      quoteGateChecksErrors: intent === "getLtlQuote",
    });

    // Re-derived fresh on every Get Quote / Save click, exactly like the missing-field
    // checks above, so a stale saved-quote date is caught even if a later edit to a
    // sibling dock field already cleared whatever handleFieldChange had set on it.
    const pickupDateIsPast = isPastPickupDate(fields.PickUpDockDate);
    if (pickupDateIsPast) {
      setErrors((prev) => ({ ...prev, PickUpDockDate: PICKUP_DATE_PAST_ERROR }));
      result.invalid.push({ key: "PickUpDockDate", label: "Pickup Date" });
      result.isValid = false;
    } else if (errors.PickUpDockDate === PICKUP_DATE_PAST_ERROR) {
      setErrors((prev) => ({ ...prev, PickUpDockDate: "" }));
    }

    const cells: Record<number, string[]> = {};
    const packageCells: Record<number, Record<number, string[]>> = {};
    result.itemIssues.forEach((issue) => {
      if (issue.columnKeys.length > 0) cells[issue.rowIndex] = issue.columnKeys;
      issue.packageIssues.forEach((pkg) => {
        packageCells[issue.rowIndex] = {
          ...packageCells[issue.rowIndex],
          [pkg.packageIndex]: pkg.columnKeys,
        };
      });
    });
    setInvalidCells(cells);
    setInvalidPackageCells(packageCells);
    setItemsEmpty(result.itemsEmpty);

    const { ok, focusItemCell } = applyValidation(result);
    setFocusCell(focusItemCell);
    return ok;
  };

  // const isGetLTLQuotesButtonDisabled = useMemo(() => {

  //   if (state?.page === "Quick Quote" && !isQuickQuoteDataReady) {
  //     return true;
  //   }
  //   let areFieldsComplete = !getLTLQuotesMandatoryFields.some(
  //     (field) => !fields[field] || errors[field] != ""
  //   );
  //   if(state?.page === "Quick Quote" && isQuickQuoteDataReady){
  //     if(fields?.destinationZipcode && fields?.OriginZipcode && fields?.PickUpDockDate && fields?.PickUpDockTime){
  //       areFieldsComplete = true;
  //     }
  //   }

  //   const areDataFieldsValid =
  //     data?.length > 0 &&
  //     data?.every(
  //       (item) =>
  //         item.handlingUnit &&
  //         item.quantity &&
  //         item.weight &&
  //         item.itemClass &&
  //         item.itemDimension.length &&
  //         item.itemDimension.width &&
  //         item.itemDimension.height &&
  //         item.packageType
  //     );

  //   if (
  //     customerRowData?.id &&
  //     areFieldsComplete &&
  //     areDataFieldsValid
  //   ) {
  //     return false;
  //   }
  //   return true;
  // }, [
  //   customerRowData?.id,
  //   orderTypeRowData?.id,
  //   serviceSelected,
  //   fields,
  //   data,
  // ]);

  /**
   * Updates `parentCustomerId` when `customerRowData.id` changes to keep them in sync.
   */
  useEffect(() => {
    if (customerRowData && customerRowData?.id && !saveQuotationPage) {
      clearLTLQuoteDetails();
      setParentCustomerId(customerRowData.id);
      setFields((prevData) => ({
        ...prevData,
        serviceCost: "",
      }));
    }
  }, [customerRowData]);

  useEffect(() => {
    if (!saveQuotationPage) {
      setFields((prevData) => ({
        ...prevData,
        serviceCost: "",
      }));
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
    }
  }, [serviceSelected, assCustomerRowData]);

  const clearLTLQuoteDetails = () => {
    setIsSelectedParent(false);
    setShowLTLList(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  const clearFinalMileQuoteDetails = () => {
    setIsSelectedParent(false);
    setShowLTLList(false);
    setGetQuote(false);
    setIsQuote(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  useEffect(() => {
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setIsSelectedParent(false);
      setShowLTLData(false);
      setShowFMList(false);
      setShowLTLList(false);
    }
  }, [
    data,
    dropAccessorialsSelected,
    dropLimitedAccessSelected,
    pickupAccessorialsSelected,
    pickupLimitedAccessSelected,
    assCustomerRowData,
    customerRowData,
    serviceSelected,
    fields?.additionalServices,
    fields?.destinationNoOfFlights,
    fields?.destinationExtramen,
    fields?.destinationStorage,
    fields?.destinationWaitTimeMinutes,
  ]);
  useEffect(() => {
    setNotLTL(false);
    setOrderTypeRowData({
      id: ltlOrderType?.id,
      name: "LTL",
      description: "Less than Truck Load",
    });
  }, []);
  useEffect(() => {
    if (orderTypeRowData?.name === "LTL + Final Mile") {
      setIsLTLFinalMile(true);
      if (state?.page !== "Quick Quote") setLtlFMQuoteSelected(true);
    }
  }, [orderTypeRowData]);

  const getAccessorialsList = async () => {
    try {
      setLoading(true);
      let url = `/order/ltl-accessorial/wwex`;
      const agentResponse = await getApi<any>(url);
      if (agentResponse.success) {
        const data = agentResponse.data || [];
        const pickup = data.filter((item: any) => item?.isPickup);
        setPickupAccessorialDropdownData(pickup);
        const drop = data.filter((item: any) => item?.isDelivery);
        setDropAccessorialDropdownData(drop);
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getServiceList = async (orderType: string) => {
    try {
      setLoading(true);
      orderType = orderType.split(" + ").join(",");
      let url = `/agent/service/?limit=15&page=1&orderTypeId=${orderType}`;
      const agentResponse = await getApi<any>(url);
      if (agentResponse.success) {
        setServiceList(agentResponse?.data);
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };
  /**
   * useEffect Hook - Fetches and updates the destination address details
   * (city, state, country) whenever the user changes the zipcode.
   */
  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
    }
  }, [fields?.destinationZipcode]);

  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
    }
  }, [fields?.OriginZipcode]);
  /**
   * useEffect Hook - Runs on component mount to fetch country codes, agent service
   * list, and updates the customer data based on user role.
   */
  useEffect(() => {
    if (!isUserOfOmniMove && userRole === "customer") {
      setCustomerRowData({
        id: localStorage.getItem(CUSTOMER_ID) ?? "",
        name: "",
        customerNumber: "",
        customerLevel: "",
      });
    }
    if (state?.page === "Save Quotation") {
      setSaveQuotationPage(true);
      getQuotationDetails(state.id, state?.quoteId);
    }
    if (state?.page === "Quick Quote") {
      setSaveQuotationPage(true);
      // Remember the quote's reference2 and read the customer's current Order
      // Reference 2 configuration, so the prefill effect can decide between the two.
      loadedOrderReference2Ref.current = state?.data?.reference2 ?? "";
      fetchOrderReference2Config(
        state?.data?.customer?.id,
        state?.data?.associatedCustomer?.id
      ).then(setOrderReference2Config);
      populateQuoteFromRawData(state);
      if (!state?.selectedRow?.quoteFrom?.includes("banyan")) {
        setShowLTLData(true);
      }
      // setShowLTLData(true);
      setShowFMList(true);
      setIsQuote(true);
      setIsQuickQuoteDataReady(true);
    }
  }, [state]);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getQuotationDetails = async (quoteGroupId: string, quoteId: String) => {
    try {
      setLoading(true);
      let url = `/order/order-quote/detailsavedquotation?quoteGroupById=${quoteGroupId}&quoteId=${quoteId}`;
      const quoteResponse = await getApi<any>(url);
      if (quoteResponse.success) {
        const data = quoteResponse?.data?.[quoteGroupId]?.[0] || [];
        dropHoursAreDefaultRef.current = {
          open: data?.orderType?.name !== "LTL + Final Mile" && !formatTimeForDisplay(data?.ltlDropDock?.dropOpenTime),
          close: data?.orderType?.name !== "LTL + Final Mile" && !formatTimeForDisplay(data?.ltlDropDock?.dropCloseTime),
        };
        setSavedData(data);
        if (data?.masterHub?.name) {
          setHasMasterHub(true);
          setMasterHubName(data?.masterHub?.name);
        }
        if (data?.masterHub?.address) {
          setMasterHubAddress(data?.masterHub?.address);
        }
        // Remember the quote's reference2 and read the customer's current Order
        // Reference 2 configuration from the API — the quote only holds a snapshot,
        // so a value changed since the quote was saved would otherwise be missed.
        // Both ids are fetched explicitly: the parent has to be configured too, so
        // that removing the sub-customer hands Order Reference 2 back to it.
        const isThirdLevel = customerLevel === "first" && data?.customer?.customerLevel === "third";
        loadedOrderReference2Ref.current = data?.reference2 ?? "";
        setOrderReference2Config(
          await fetchOrderReference2Config(
            data?.customer?.id,
            data?.customer?.parentCustomer?.id
          )
        );

        if (isThirdLevel) {
          const parentId = data?.customer?.parentCustomer?.id ?? "";
          setCustomerRowData({
            id: parentId,
            name: data?.customer?.parentCustomer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.parentCustomer?.customerLevel ?? "",
          });
          setParentCustomerId(parentId);
          if (data?.customer) {
            setAssCustomerNameDropdown([data.customer]);
          }
          setTimeout(() => {
            setAssCustomerRowData({
              id: data?.customer?.id ?? "",
              name: data?.customer?.name ?? "",
              customerNumber: "",
              customerLevel: data?.customer?.customerLevel ?? "",
            });
          }, 300);
        } else {
          setCustomerRowData({
            id: data?.customer?.id ?? "",
            name: data?.customer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.customerLevel ?? "",
          });
          if (data?.customer?.customerLevel === "second") {
            setParentCustomerId(data?.customer?.id ?? "");
          }
        }
        setOrderTypeRowData({
          id: data?.orderType?.id ?? "",
          name: data?.orderType?.name ?? "",
          description: data?.orderType?.description ?? "",
        });
        setServiceSelected({
          id: data?.service?.id ?? "",
          name: data?.service?.name ?? "",
        });
        let refArray = [
          {
            referenceCount: 2,
            referenceValue: data?.reference2 ?? "",
          },
        ];
        if (data?.reference3) {
          refArray.push({
            referenceCount: 3,
            referenceValue: data?.reference3 ?? "",
          });
        }
        if (data?.reference4) {
          refArray.push({
            referenceCount: 4,
            referenceValue: data?.reference4 ?? "",
          });
        }
        if (data?.reference5) {
          refArray.push({
            referenceCount: 5,
            referenceValue: data?.reference5 ?? "",
          });
        }

        setMoreReferences(refArray);
        setFields((prevData) => ({
          ...prevData,
          destinationAddress: data?.destinationAddress?.address ?? "",
          destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
          destinationZipcode: data?.destinationAddress?.zipCode ?? "",
          destinationCity: data?.destinationAddress?.city ?? "",
          destinationState: data?.destinationAddress?.state ?? "",
          destinationCountry: data?.destinationAddress?.country ?? "",
          destinationBusinessLocation:
            data?.destinationAddress?.businessLocation ?? "",
          destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
          destinationConsigneePhoneExt:
            data?.destinationAddress?.countryCallingCode ?? "+1",
          destinationConsigneePhoneNumber:
            data?.destinationAddress?.phoneNumber ?? "",
          destinationConsigneeContactEmail:
            data?.destinationAddress?.contactEmail ?? "",
          destinationExtramen: data?.destinationAddress?.extraMen ?? "",
          destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
          destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
          destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
          destinationWeight: data?.destinationAddress?.weight ?? "",
          destinationSpecialInstructions:
            data?.destinationAddress?.specialInstructions ?? "",
          orderReference: data?.reference ?? "",
          orderReference2: data?.reference2 ?? "",
          orderReference3: data?.reference3 ?? "",
          orderReference4: data?.reference4 ?? "",
          orderReference5: data?.reference5 ?? "",
          additionalServices: data?.additionalServices ?? [],
          selectedServiceId: data?.service?.id ?? "",
          OriginAddress: data?.originAddress?.address ?? "",
          OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
          OriginZipcode: data?.originAddress?.zipCode ?? "",
          OriginCity: data?.originAddress?.city ?? "",
          OriginState: data?.originAddress?.state ?? "",
          OriginCountry: data?.originAddress?.country ?? "",
          OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
          OriginConsigneeName: data?.originAddress?.contactName ?? "",
          OriginConsigneePhoneExt:
            data?.originAddress?.countryCallingCode ?? "+1",
          OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
          OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
          PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickUpDockDate: data?.lTLPickUpDock?.pickUpDate
            ? normalizeDate(data?.lTLPickUpDock?.pickUpDate)
            : moment().format("YYYY-MM-DD"),
          PickUpDockTime: data?.lTLPickUpDock?.pickUpDate
            ? extractTime(data?.lTLPickUpDock?.pickUpDate)
            : "08:00 AM",
          PickUpDockName: data?.lTLPickUpDock?.dockName ?? "",
          PickUpDOckNumber: data?.lTLPickUpDock?.pickupNumber ?? "",
          PickUpDockOpenTime:
            data?.lTLPickUpDock?.dockOpenTime === "Invalid date"
              ? DEFAULT_DOCK_OPEN_TIME
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockOpenTime) || DEFAULT_DOCK_OPEN_TIME,
          PickUpDOckCloseTime:
            data?.lTLPickUpDock?.dockCloseTime === "Invalid date"
              ? DEFAULT_DOCK_CLOSE_TIME
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
          PickUpDOckNote: data?.lTLPickUpDock?.note ?? "",
          PickUpFCFS: data?.lTLPickUpDock?.firstComeFirstServe ?? "",
          LTLDropAccessorials: [],
          DropDockName: data?.ltlDropDock?.dockName ?? "",
          DropDOckNumber: data?.ltlDropDock?.pickupNumber ?? "",
          DropDockOpenTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropOpenTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropOpenTime) || DEFAULT_DOCK_OPEN_TIME,
          DropDOckCloseTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropCloseTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
          DropDOckNote: data?.ltlDropDock?.note ?? "",
          DropFCFS: data?.ltlDropDock?.firstComeFirstServe ?? false,
        }));

        // Add this right after the setFields block:
        setAddressSelectedOrigin({
          id: data?.originAddress?.id || "",
          address: data?.originAddress?.address || "",
          aptSuit: data?.originAddress?.aptSuit || "",
          zipCode: data?.originAddress?.zipCode || "",
          city: data?.originAddress?.city || "",
          state: data?.originAddress?.state || "",
          country: data?.originAddress?.country || "",
          businessLocation: data?.originAddress?.businessLocation || "",
          contactPerson: data?.originAddress?.contactName || "",
          phoneNumber: data?.originAddress?.phoneNumber || "",
          countryCallingCode: data?.originAddress?.countryCallingCode || "+1",
          contactEmail: data?.originAddress?.contactEmail || "",
        });

        setAddressSelected({
          id: data?.destinationAddress?.id || "",
          address: data?.destinationAddress?.address || "",
          aptSuit: data?.destinationAddress?.aptSuit || "",
          zipCode: data?.destinationAddress?.zipCode || "",
          city: data?.destinationAddress?.city || "",
          state: data?.destinationAddress?.state || "",
          country: data?.destinationAddress?.country || "",
          businessLocation: data?.destinationAddress?.businessLocation || "",
          contactPerson: data?.destinationAddress?.contactName || "",
          phoneNumber: data?.destinationAddress?.phoneNumber || "",
          countryCallingCode: data?.destinationAddress?.countryCallingCode || "+1",
          contactEmail: data?.destinationAddress?.contactEmail || "",
        });
        const mapOrderData = (userData: any) =>
          userData.map((user: any, index: number) => ({
            id: user?.id || "",
            serial: userData.length - index,
            density: user?.density || "",
            cbm: user?.cbm || "",
            description: user?.description || "",
            handlingUnit: user?.handlingUnit || "",
            quantity: user?.quantity || "",
            weight: user?.weight || "",
            itemDimension: {
              length: user?.length || "",
              width: user?.width || "",
              height: user?.height || "",
            },
            unit: user?.unit
              ? { name: user?.unit, id: user?.unit }
              : user?.weightUnit === "kgs"
                ? { name: "CM", id: "CM" }
                : { name: "IN", id: "IN" },
            itemClass: user?.itemClass || "",
            comments: user?.comments || "",
            nmfc: user?.nmfc || "",
            skuNumber: user?.skuNumber || "",
            packageType: user?.packageType || "",
            package: user?.package || [],
            stackable:
              user?.stackable === true
                ? { name: "Yes", id: "Yes" }
                : { name: "No", id: "No" },
            weightUnit: { name: user?.weightUnit, id: user?.weightUnit },
            hazmat: user?.hazmats?.unno ? true : false,
            linearFeet: user?.linearFeet,
            hazardMaterial: user?.hazmats?.unno
              ? {
                itemName: user.hazmats?.itemName || "",
                packageType:
                  typeof user.hazmats?.packageType === "object"
                    ? user.hazmats?.packageType?.name
                    : user.hazmats?.packageType || "",
                hazmatClass: user.hazmats?.hazmatClass || "",
                unno: user.hazmats?.unno || "",
                properShippingName: user.hazmats?.properShippingName || "",
                packagingGroup: user.hazmats?.packagingGroup || "",
                contact: user.hazmats?.contact || "",
                phoneNumber: user.hazmats?.phoneNumber || "",
                countryCallingCode: user.hazmats?.countryCallingCode || "",
                poison: user.hazmats?.poison ? true : false,
                isReportableQuantity: user.hazmats?.isReportableQuantity
                  ? true
                  : false,
                isMarinePollutant: user.hazmats?.isMarinePollutant
                  ? true
                  : false,
              }
              : {},
          }));
        setData(mapOrderData([...(data?.orderItem || [])].reverse()));
        if (
          data?.orderType?.name === "Final Mile" &&
          expiredData(data?.createdAt) &&
          !data?.isExpiry
        ) {
          let QuoteDataArr = [];
          const mappedData = (qData: any) => {
            const services = quoteResponse?.data?.[quoteGroupId].map(
              (item: any, serviceIndex: number) => ({
                id: item?.id,
                serial: serviceIndex + 1,
                serviceId: item?.service?.id,
                serviceName: item?.service?.name || "",
                description: item?.service?.description || "",
                serviceLevel: item?.serviceType?.name,
                quotePrice: item?.pricing?.total || 0.0,
                fuelSurcharge: item?.rateDetails?.fuelSurcharge || 0.0,
                additionalOrderCharges:
                  item?.rateDetails?.additionalOtherCharges,
                basePrice: item?.rateDetails?.basePrice || 0.0,
                totalWeight: item?.totalWeight || 0,
                deliverWithin: item?.deliverWithin || 0,
                distanceToDestination: item?.distanceToDestination || 0,
                shortName: item?.service?.shortName || "",
                longName: item?.service?.longName || "",
                useOver150Lbs: item?.service?.useOver150Lbs || false,
                address: hasMasterHub
                  ? masterHubAddress
                  : item?.agentHub?.address || "",
                operationHrsFrom: hasMasterHub
                  ? masterHubAddress?.operationHrsFrom
                  : item.agentHub?.address?.operationHrsFrom || 0,
                operationHrsTo: hasMasterHub
                  ? masterHubAddress?.operationHrsTo
                  : item.agentHub?.address?.operationHrsTo || 0,
                hubName: hasMasterHub
                  ? masterHubName
                  : item.agentHub?.name || "",
                // hubName: item.agentHub?.name || "",
                agentHubId: item.agentHub?.id || "",
                addressName: hasMasterHub
                  ? masterHubAddress.address
                  : item.agentHub?.address || "",
                agentName: item.agentHub?.agent?.name || "",
                quoteId: item?.id,
                allQuoteId: [item?.id],
              })
            );
            const info: QuoteInfo = {
              agentName: qData.agentHub?.agent?.name || "",
              operationHrsFrom: hasMasterHub
                ? masterHubAddress?.operationHrsFrom
                : qData.agentHub?.operationHrsFrom || "",
              operationHrsTo: hasMasterHub
                ? masterHubAddress?.operationHrsTo
                : qData.agentHub?.operationHrsTo || "",
              hubName: hasMasterHub
                ? masterHubName
                : qData.agentHub?.name || "",
              addressName: hasMasterHub
                ? masterHubAddress?.address
                : qData.agentHub?.address || "",
              address: hasMasterHub
                ? masterHubAddress?.address
                : qData?.agentHub?.address || "",
              distanceToDestination: qData?.distanceToDestination || 0,
            };

            return { info, services };
          };
          QuoteDataArr.push(mappedData(data));
          SetQuoteData(QuoteDataArr);
          setGetQuote(true);
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          destinationZipcode: "",
          OriginZipcode: "",
          DropDockDate: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
          destinationConsigneePhoneNumber: "",
          OriginConsigneePhoneNumber: "",
        }));
        if (
          data?.orderType?.name === "LTL" ||
          data?.orderType?.name === "LTL + Final Mile"
        ) {
          setPickupAccessorialsSelected(data?.originAddress?.accessorials);
          setDropAccessorialsSelected(data?.destinationAddress?.accessorials);
          setPickupLimitedAccesssSelected({
            id: data?.originAddress?.locationType?.id,
            name: data?.originAddress?.locationType?.name,
          });
          setDropLimitedAccesssSelected({
            id: data?.destinationAddress?.locationType?.id,
            name: data?.destinationAddress?.locationType?.name,
          });
        }
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  // LTL + Final Mile disables the drop dock inputs but still sends whatever they hold.
  // Only the default hours filled in by this form are taken back out on switching to it,
  // so the drop window it sends is exactly what it was before the defaults existed.
  // Anything typed, loaded from a quote or picked from the address book is left as is.
  useEffect(() => {
    const isDefault = dropHoursAreDefaultRef.current;
    if (orderTypeRowData?.name === "LTL + Final Mile") {
      setFields((prev) => ({
        ...prev,
        DropDockOpenTime: isDefault.open ? "" : prev.DropDockOpenTime,
        DropDOckCloseTime: isDefault.close ? "" : prev.DropDOckCloseTime,
      }));
      dropHoursAreDefaultRef.current = { open: false, close: false };
    } else {
      setFields((prev) => {
        if (!prev.DropDockOpenTime) isDefault.open = true;
        if (!prev.DropDOckCloseTime) isDefault.close = true;
        return {
          ...prev,
          DropDockOpenTime: prev.DropDockOpenTime || DEFAULT_DOCK_OPEN_TIME,
          DropDOckCloseTime: prev.DropDOckCloseTime || DEFAULT_DOCK_CLOSE_TIME,
        };
      });
    }
  }, [orderTypeRowData?.name]);

  const populateQuoteFromRawData = (state: any) => {
    const data = state?.data;
    dropHoursAreDefaultRef.current = {
      open: data?.orderType?.name !== "LTL + Final Mile" && !formatTimeForDisplay(data?.dropDock?.dropOpenTime),
      close: data?.orderType?.name !== "LTL + Final Mile" && !formatTimeForDisplay(data?.dropDock?.dropCloseTime),
    };
    setFields((prevData: any) => ({
      ...prevData,
      destinationAddress: data?.destinationAddress?.address ?? "",
      destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
      destinationZipcode: data?.destinationAddress?.zipCode ?? "",
      destinationCity: data?.destinationAddress?.city ?? "",
      destinationState: data?.destinationAddress?.state ?? "",
      destinationCountry: data?.destinationAddress?.country ?? "",
      destinationBusinessLocation:
        data?.destinationAddress?.businessLocation ?? "",
      destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
      destinationConsigneePhoneExt:
        data?.destinationAddress?.countryCallingCode ?? "+1",
      destinationConsigneePhoneNumber:
        data?.destinationAddress?.phoneNumber ?? "",
      destinationConsigneeContactEmail:
        data?.destinationAddress?.contactEmail ?? "",
      destinationExtramen: data?.destinationAddress?.extraMen ?? "",
      destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
      destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
      destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
      destinationWeight: data?.destinationAddress?.weight ?? "",
      destinationSpecialInstructions:
        data?.destinationAddress?.specialInstructions ?? "",
      orderReference: data?.reference ?? "",
      orderReference2: data?.reference2 ?? "",
      orderReference3: data?.reference3 ?? "",
      orderReference4: data?.reference4 ?? "",
      orderReference5: data?.reference5 ?? "",
      additionalServices: data?.additionalServices ?? [],
      selectedServiceId: data?.service?.id ?? "",
      OriginAddress: data?.originAddress?.address ?? "",
      OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
      OriginZipcode: data?.originAddress?.zipCode ?? "",
      OriginCity: data?.originAddress?.city ?? "",
      OriginState: data?.originAddress?.state ?? "",
      OriginCountry: data?.originAddress?.country ?? "",
      OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
      OriginConsigneeName: data?.originAddress?.contactName ?? "",
      OriginConsigneePhoneExt: data?.originAddress?.countryCallingCode ?? "+1",
      OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
      OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
      PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
        ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
        : "",
      PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
        ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
        : "",
      PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
        ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
        : "",
      PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
        ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
        : "",
      PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
        ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
        : "",
      PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
        ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
        : "",
      PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
        ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
        : "",
      PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
        ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
        : "",
      PickUpDockDate: data?.pickUpDock?.pickUpDate
        ? normalizeDate(data?.pickUpDock?.pickUpDate)
        : moment().format("YYYY-MM-DD"),
      PickUpDockTime: data?.pickUpDock?.pickUpDate
        ? extractTime(data?.pickUpDock?.pickUpDate)
        : "08:00 AM",
      PickUpDockName: data?.pickUpDock?.dockName,
      PickUpDOckNumber: data?.pickUpDock?.pickupNumber,
      PickUpDockOpenTime: formatTimeForDisplay(data?.pickUpDock?.dockOpenTime) || DEFAULT_DOCK_OPEN_TIME,
      PickUpDOckCloseTime: formatTimeForDisplay(data?.pickUpDock?.dockCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
      PickUpDOckNote: data?.pickUpDock?.note ?? "",
      PickUpFCFS: data?.pickUpDock?.firstComeFirstServe,
      LTLDropAccessorials: [],
      DropDockName: "",
      DropDOckNumber: "",
      DropDockOpenTime:
        data?.orderType?.name === "LTL + Final Mile"
          ? ""
          : formatTimeForDisplay(data?.dropDock?.dropOpenTime) || DEFAULT_DOCK_OPEN_TIME,
      DropDOckCloseTime:
        data?.orderType?.name === "LTL + Final Mile"
          ? ""
          : formatTimeForDisplay(data?.dropDock?.dropCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
      DropDOckNote: "",
      DropFCFS: false,
      MidStopAddress: data?.MidStopAddress || "",
      MidStopAPTSuit: data?.MidStopAPTSuit || "",
      MidStopZipcode: data?.MidStopZipcode || "",
      MidStopCity: data?.MidStopCity || "",
      MidStopState: data?.MidStopState || "",
      MidStopCountry: data?.MidStopCountry || "",
      MidStopContactName: data?.MidStopContactName || "",
      MidStopPhoneNumber: data?.MidStopPhoneNumber || "",
      MidStopPhoneNumberExt: data?.MidStopPhoneNumberExt || "+1",
      MidStopContactEmail: data?.MidStopContactEmail || [],
      agentHubId: data?.agentHubId,
      // selectedServiceId: data?.selectedServiceId,
      selectedQuoteId: data?.selectedQuoteId,
      allQuoteId: data?.allQuoteId,
      CarrierName: data?.CarrierName,
      serviceCost: data?.serviceCost,
      scac: data?.scac,
      distance: data?.distance,
      offerId: data?.offerId,
      // costDetails: additionalCharges(row),
      markup: data?.markup,
      productTransactionId: data?.productTransactionId,
      customerMarkupType: data?.customerMarkupType,
      plProvider: "",
      courierPro1: "",
      courierPro2: "",
      //Final mile box details
      agentName: data?.agentName,
      hubName: data?.hubName,
      city: data?.city,
      zipCode: data?.zipCode,
      country: data?.country,
      AgentHubId: data?.AgentHubId,
      distanceToDestination: data?.distanceToDestination || 0,
      operationHrsFrom: data?.operationHrsFrom || "00:00",
      operationHrsTo: data?.operationHrsTo || "23:59",
      quotePrice: data?.quotePrice || 0,
      source: state?.selectedData?.source,
      orderFor: state?.selectedData?.orderFor
    }));
    // Set customer, associated customer, order type, and service
    if (data?.customer) {
      setCustomerRowData(data?.customer);
      if (data?.customer?.id) setParentCustomerId(data?.customer?.id);
    }
    // Delay setting the sub-customer so the MutliDropdown's parentCustomerId useEffect
    // (which calls handleRemoveRowData to clear the selection) fires first.
    if (data?.associatedCustomer?.id) {
      setTimeout(() => {
        setAssCustomerRowData(data?.associatedCustomer);
      }, 300);
    }
    if (data?.orderType) setOrderTypeRowData(data?.orderType);
    if (data?.service) setServiceSelected(data?.service);
    if (data?.pickupAccessorials?.length > 0)
      setPickupAccessorialsSelected(data?.pickupAccessorials);
    else if (data?.originAddress?.accessorials?.length > 0)
      setPickupAccessorialsSelected(data?.originAddress?.accessorials);
    if (data?.dropAccessorials?.length > 0)
      setDropAccessorialsSelected(data?.dropAccessorials);
    else if (state?.ltlAccessorialData?.dropAccessorialsSelected?.length > 0)
      setDropAccessorialsSelected(
        state?.ltlAccessorialData?.dropAccessorialsSelected
      );
    if (state?.ltlAccessorialData?.pickupLimitedAccessSelected)
      setPickupLimitedAccesssSelected(
        state?.ltlAccessorialData?.pickupLimitedAccessSelected
      );
    if (state?.ltlAccessorialData?.dropLimitedAccessSelected)
      setDropLimitedAccesssSelected(
        state?.ltlAccessorialData?.dropLimitedAccessSelected
      );
    // Map items
    const mapOrderData = (items: any[]) =>
      items.map((item, index) => ({
        id: item?.id || item?.descriptionId || "",
        descriptionId: item?.id,
        serial: items.length - index,
        density: item?.density || "",
        cbm: item?.cbm || "",
        description: item?.description || "",
        handlingUnit: item?.handlingUnit || "",
        quantity: item?.quantity || "",
        weight: item?.weight || "",
        itemDimension: {
          length: item?.length || "",
          width: item?.width || "",
          height: item?.height || "",
        },
        unit: item?.unit
          ? { name: item.unit, id: item.unit }
          : item?.weightUnit === "kgs"
            ? { name: "CM", id: "CM" }
            : { name: "IN", id: "IN" },
        itemClass: item?.itemClass || "",
        comments: item?.comments || "",
        nmfc: item?.nmfc || "",
        skuNumber: item?.skuNumber || "",
        packageType: item?.packageType || "",
        package: item?.package || [],
        stackable:
          item?.stackable === true
            ? { name: "Yes", id: "Yes" }
            : { name: "No", id: "No" },
        weightUnit: { name: item?.weightUnit, id: item?.weightUnit },
        hazmat: item?.hazardMaterial?.itemName ? true : false,
        linearFeet: item?.linearFeet,
        hazardMaterial: item?.hazardMaterial || {},
      }));
    setData(mapOrderData(data?.items || []));
    // Set quoteData if pricing and service info exist
    if (data?.pricing && data?.service) {
      const quoteInfo: QuoteInfo = {
        agentName: data?.agentHub?.agent?.name || "",
        operationHrsFrom: hasMasterHub
          ? masterHubAddress?.operationHrsFrom
          : data?.agentHub?.operationHrsFrom || "",
        operationHrsTo: hasMasterHub
          ? masterHubAddress?.operationHrsTo
          : data?.agentHub?.operationHrsTo || "",
        hubName: hasMasterHub ? masterHubName : data?.agentHub?.name || "",
        addressName: hasMasterHub
          ? masterHubAddress?.address
          : data?.agentHub?.address?.address || "",
        address: hasMasterHub
          ? masterHubAddress
          : data?.agentHub?.address || {},
        distanceToDestination: data?.distanceToDestination || 0,
      };

      const services = [
        {
          id: data?.id,
          serial: 1,
          serviceId: data?.service?.id,
          serviceName: data?.service?.name || "",
          description: data?.service?.description || "",
          serviceLevel: data?.serviceType?.name || "",
          quotePrice: data?.pricing?.total || 0.0,
          fuelSurcharge: data?.rateDetails?.fuelSurcharge || 0.0,
          additionalOrderCharges: data?.rateDetails?.additionalOtherCharges,
          basePrice: data?.rateDetails?.basePrice || 0.0,
          totalWeight: data?.totalWeight || 0,
          deliverWithin: data?.deliverWithin || 0,
          distanceToDestination: data?.distanceToDestination || 0,
          shortName: data?.service?.shortName || "",
          longName: data?.service?.longName || "",
          useOver150Lbs: data?.service?.useOver150Lbs || false,
          address: hasMasterHub
            ? masterHubAddress
            : data?.agentHub?.address || {},
          operationHrsFrom: hasMasterHub
            ? masterHubAddress?.operationHrsFrom
            : data?.agentHub?.address?.operationHrsFrom || "",
          operationHrsTo: hasMasterHub
            ? masterHubAddress?.operationHrsTo
            : data?.agentHub?.address?.operationHrsTo || "",
          hubName: hasMasterHub ? masterHubName : data?.agentHub?.name || "",
          agentHubId: data?.agentHub?.id || "",
          addressName: hasMasterHub
            ? masterHubAddress?.address
            : data?.agentHub?.address?.address || "",
          agentName: data?.agentHub?.agent?.name || "",
          quoteId: data?.id,
          allQuoteId: [data?.id],
        },
      ];

      SetQuoteData([{ info: quoteInfo, services }]);
      setGetQuote(true);
    }
  };

  const getApiPackageData = async () => {
    const url = `/service/packagetype?from=order&finalMileLTL=both`;
    const apiResponse = await getApi<any>(url);

    if (!apiResponse?.success) return;

    const packageData = apiResponse?.data?.ltl
      ?.filter((item: any) => item.isActive && !item.isDeleted)
      ?.map((item: any) => ({
        id: item.id,
        name: item.packageCode || "",
      }));
    setApiPackageType(packageData);
  };

  useEffect(() => {
    // if (orderTypeRowData?.name === "LTL + Final Mile") {
    //   setDropAccessorialsSelected([]);
    //   setDropLimitedAccesssSelected({ id: "", name: "" });
    //   fields.DropFCFS = false;
    // }
    if (
      orderTypeRowData?.name === "Expedited" ||
      orderTypeRowData?.name === "Final Mile" ||
      orderTypeRowData?.name === "LTL" ||
      orderTypeRowData?.name === "LTL + Final Mile" ||
      orderTypeRowData?.name === ""
    ) {
      getApiPackageData();
    }
  }, [orderTypeRowData?.name]);

  useEffect(() => {
    if (state?.page !== "Save Quotation") {
      setMasterHubName("");
      setMasterHubAddress({});
    }
  }, [getQuote]);

  /**
   * Adds a new reference object to the `moreReferences` array if the current
   * number of references is less than 4. Each new reference object will
   * have a `referenceCount` that increments based on the current length
   * of the `moreReferences` array and a default `referenceValue` set to an
   * empty string.
   *
   * The function uses optional chaining to safely access the length of
   * `moreReferences`. The `referenceCount` for the new reference is set
   * to one more than the current length of the array (plus one to adjust
   * for zero-based indexing).
   *
   * @function addMoreReferences
   * @returns {void} This function does not return a value. It updates the
   * state of `moreReferences` directly by calling `setMoreReferences`.
   */
  const addMoreReferences = () => {
    if (moreReferences?.length < 4) {
      let references = [...moreReferences];
      references.push({
        referenceCount: 1 + moreReferences?.length + 1,
        referenceValue: "",
      });
      setMoreReferences(references);
    }
  };

  /**
   * Removes a reference from the moreReferences array at the specified index.
   *
   * This function updates the state of moreReferences by removing the
   * reference at the given index, and it adjusts the referenceCount for
   * the remaining references to ensure they are sequentially numbered
   * starting from 2.
   *
   * @param index - The index of the reference to be removed from the moreReferences array.
   *
   * @returns {void} This function does not return a value. It updates the state of moreReferences.
   *
   * @throws {Error} Throws an error if moreReferences is undefined or not an array.
   */
  const removeReferences = (index: number) => {
    if (moreReferences?.length > 1) {
      let newRefArray: MoreReferencesType[] = [];
      let refIndex = 0;
      moreReferences.map((item, mIndex) => {
        if (index !== mIndex) {
          newRefArray.push({
            referenceCount: refIndex + 2,
            referenceValue: item.referenceValue,
          });
          refIndex++;
        }
      });
      setMoreReferences(newRefArray);
    }
  };

  /**
   * Handles changes to a reference field in a list of more references.
   *
   * This function updates the value of a specific reference based on the provided
   * index and data. It creates a new array of references with the updated value
   * while keeping the other references unchanged.
   *
   * @param {string} data - The new value to set for the reference at the specified index.
   * @param {number} index - The index of the reference to be updated.
   *
   * @returns {void} - This function does not return a value. It updates the state
   *                   of moreReferences using the setMoreReferences function.
   */
  const handleRefFieldChange = (data: string, index: number) => {
    let newRefArray: MoreReferencesType[] = [];
    moreReferences.map((item, mIndex) => {
      newRefArray.push({
        referenceCount: item.referenceCount,
        referenceValue: index === mIndex ? data : item.referenceValue,
      });
    });
    setMoreReferences(newRefArray);
  };

  /**
   * Handles changes in checkbox fields and updates the form values state.
   *
   * @param e - The checkbox change event.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target as HTMLInputElement;
    if (!notLTL) {
      setFields((prevData: any) => ({ ...prevData, [id]: !prevData[id] }));
    } else {
      if (checked) {
        // Mutually exclusive: selecting one removes the other
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: [id],
        }));
      } else {
        // Uncheck: remove this option
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: prevValues.additionalServices.filter(
            (x) => x !== id
          ),
        }));
      }
    }
  };


  const handleSaveQuotation = async (quoteId: string) => {
    try {

      setLoading(true);
      const apiPayload = {
        quoteId: quoteId,
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
        },
        originAddress: null,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        additionalServices: fields.additionalServices,
      };
      const apiResponse = await postApi(SAVE_QUOTATION, apiPayload);
      if (apiResponse.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const SAVE_QUOTATION_FOR_LTL = "/order/order-quote/savedquotationforltl";

  // ---- Optional small helpers (co-locate with your other utils/helpers) ----
  const asNum = (v: any, fallback = 0) => {
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : fallback;
  };
  const asStr = (v: any, fallback = "") =>
    (typeof v === "string" ? v : v?.toString?.() || "") || fallback;
  const onlyDigits = (s?: string) => asStr(s).replace(/\D/g, "");

  // If you already have mapItemsDataToPayload, keep it; else use this LTL-specific mapper:
  const mapItemsDataToPayloadForLTL = (items: any[]) =>
    (items || []).map((it) => ({
      description: asStr(it.description),
      handlingUnit: asStr(it.handlingUnit ?? it.handlingUnitAmt ?? it.hu),
      quantity: asStr(it.quantity ?? it.qty),
      weight: asStr(it.weight),
      weightUnit: asStr(it.weightUnit ?? "lbs"),
      length: asStr(it?.length || it?.itemDimension?.length),
      width: asStr(it?.width || it?.itemDimension?.width),
      height: asStr(it?.height || it?.itemDimension?.height),
      itemClass: asStr(it.itemClass),
      comments: asStr(it.comments),
      nmfc: asStr(it.nmfc),
      skuNumber: asStr(it.skuNumber),
      packageType: asStr(it.packageType ?? it.package_type ?? it.package),
      package: Array.isArray(it.package) ? it.package : [],
      stackable: Boolean(it.stackable),
      linearFeetStackable: asNum(it.linearFeetStackable, 0),
      linearFeet: asNum(it.linearFeet),
      density: asNum(it.density),
      cbm: asNum(it.cbm),
      hazardMaterial: it.hazardMaterial ?? {}, // keep structure if present
    }));

  // If you keep multiple quotes in state, adapt this to your source of truth.
  // Fallbacks: fields.quoteArray or an empty array.

  const buildQuoteArray = () => {
    // Persist exactly what the quote table shows: with "LTL Only" checked the
    // saved quote keeps only the LTL offers, otherwise every offer is saved.
    const quotesToSave = filterLtlOnlyQuotes(quoteData, isLtlOnly);
    if (quotesToSave.length === 0) return [];

    // De-duplicate by the offer's whole identity — see dedupeKey below.
    const seen = new Set<string>();

    return quotesToSave
      .map((q: any) => {
        const offered =
          q?.quoteFrom === "worldwide"
            ? Array.isArray(q.offeredProductList)
              ? q.offeredProductList[0]
              : null
            : null;

        // Prefer the most customer-facing total; fall back sensibly
        //In case of "LTL + Final Mile" we are using  q?.totalOfferPriceWithMarkupAndFinalMile KEY
        const total = asNum(
          q?.orderTypeName === "LTL + Final Mile" ? q?.totalOfferPriceWithMarkupAndFinalMile :
            q.totalPrice ??
            offered?.offerPrice?.value ??
            q.totalOfferPrice?.value ??
            0
        );

        // Alliance carries its own id pair and spells transit days `TransitDays`,
        // and matches none of the branches below, so without these it falls to the
        // internal-LTL defaults: a blank shipmentOfferId drops the whole quote at
        // the guard further down, and carrier name/code save empty.
        const isAlliance = q?.quoteFrom === "alliance";

        const transitDays = asNum(
          q?.quoteFrom === "banyan"
            ? q?.transitTime
            : q?.quoteFrom === "worldwide"
              ? offered?.shopRQShipment?.timeInTransit?.transitDays
              : isAlliance
                ? q?.TransitDays
                : q?.transitDays,
          0
        );

        const carrierName = asStr(
          (q?.quoteFrom === "banyan" || q?.quoteFrom === "providence" || q?.quoteFrom === "armstrong" || isAlliance)
            ? q?.carrierName
            : q?.quoteFrom === "worldwide"
              ? q.primaryVendor?.preferredName
              : q?.hub?.agent?.name
        );

        const carrierCode = asStr(
          q?.quoteFrom === "banyan"
            ? q?.scac
            : q?.quoteFrom === "worldwide"
              ? q.primaryVendor?.scac
              : q?.quoteFrom === "providence"
                ? q?.scac
                : q?.quoteFrom === "armstrong" || isAlliance
                  ? q?.scac
                  : q?.hub?.agent?.carrierCode,
          ""
        );

        const shipmentOfferId = asStr(
          q?.quoteFrom === "banyan"
            ? q?.quoteId
            : q?.quoteFrom === "worldwide"
              ? q.offerId
              : q?.quoteFrom === "providence"
                ? q?.quoteDetailId
                : q?.quoteFrom === "armstrong"
                  ? q?.rateRequestLocator
                  : isAlliance
                    ? q?.shipmentOfferId
                    : q?.services?.[0]?.quoteId

        ); // ← becomes shipmentOfferId in payload
        const productTransactionId = asStr(
          q?.quoteFrom === "banyan"
            ? q?.quoteId
            : q?.quoteFrom === "worldwide"
              ? q?.productTransactionId
              : q?.quoteFrom === "providence"
                ? q?.quoteDetailId
                : q?.quoteFrom === "armstrong"
                  ? q?.carrierQuoteId
                  : isAlliance
                    ? q?.shipmentProductTransactionId
                    : q?.services?.[0]?.quoteId
        );
        // Top-level order/service from your quoteData (preferred) with safe fallbacks
        const orderTypeId = asStr(q.orderId || orderTypeRowData?.id || "");
        const serviceId = asStr(q.serviceId || fields?.selectedServiceId || "");

        if (!shipmentOfferId) return null;

        // Dedupe on the offer's whole identity instead of a single id. The quote
        // table renders one row per offer and never drops any, so keying on one
        // id silently lost rows the user could see. Armstrong is the reason
        // serviceLevelType is in the key: it returns several service levels
        // under ONE carrierQuoteId (e.g. TFORCE STND and GTD_PM), so that id
        // alone collapsed them into a single saved quote. rateRequestLocator is
        // shared even more widely — it is one per rate request — and an offer
        // whose own id comes back blank used to be dropped outright.
        const dedupeKey = [
          q?.quoteFrom,
          shipmentOfferId,
          productTransactionId,
          q?.serviceLevelType,
          carrierName,
          total,
        ].join("|");
        if (seen.has(dedupeKey)) return null;
        seen.add(dedupeKey);

        return {
          orderTypeId,
          serviceId,
          shipmentOfferId,
          productTransactionId,
          pricing: {
            total,
            carrierName,
            carrierCode,
            estimatedLTLTransitDay: transitDays,
            finalmileCarrierName: q?.finalMileData?.hub?.agent?.name || "", // LTL only; intentionally blank
          },
        };
      })
      .filter(Boolean);
  };

  // ---- MAIN FUNCTION ----
  const handleSaveQuotationForLTL = async () => {
    try {
      setLoading(true);
      // Derive some basics
      const customerId = assCustomerRowData?.id
        ? assCustomerRowData?.id
        : customerRowData?.id; //asStr(customerRowData?.id) || "";
      const orderTypeId = asStr(orderTypeRowData?.id, "");

      // Items mapping (use your existing `data` source)
      const items = mapItemsDataToPayloadForLTL(data);

      // Destination and Origin blocks (mirror exact keys from sample)
      const destinationAddress = {
        address: asStr(fields.destinationAddress),
        aptSuit: asStr(fields.destinationAPTSuite),
        zipCode: asStr(fields.destinationZipcode),
        city: asStr(fields.destinationCity),
        state: asStr(fields.destinationState),
        country: asStr(fields.destinationCountry),
        businessLocation: asStr(fields.destinationBusinessLocation),
        contactName: asStr(fields.destinationConsigneeName),
        phoneNumber: onlyDigits(fields.destinationConsigneePhoneNumber),
        countryCallingCode: asStr(fields.destinationConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.destinationConsigneeContactEmail),
        extraMen: asStr(fields.destinationExtramen),
        waitTime: asStr(fields.destinationWaitTimeMinutes),
        stairsNumber: asStr(fields.destinationNoOfFlights),
        storage: asStr(fields.destinationStorage),
        handlingUnit: asNum(fields.destinationHandingUnits),
        weight: asNum(fields.destinationWeight),
        pieces: asNum(fields.destinationPieces),
        accessorials: dropAccessorialsSelected,
        locationType: dropLimitedAccessSelected,
      };

      const originAddress = {
        address: asStr(fields.OriginAddress),
        aptSuit: asStr(fields.OriginAPTSuite),
        zipCode: asStr(fields.OriginZipcode),
        city: asStr(fields.originCity),
        state: asStr(fields.OriginState),
        country: asStr(fields.OriginCountry),
        businessLocation: asStr(fields.OriginBusinessLocation),
        contactName: asStr(fields.OriginConsigneeName),
        phoneNumber: onlyDigits(fields.OriginConsigneePhoneNumber),
        countryCallingCode: asStr(fields.OriginConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.OriginConsigneeContactEmail),
        accessorials: pickupAccessorialsSelected,
        locationType: pickupLimitedAccessSelected,
      };

      // Quotes array as per the sample
      const quoteArray = buildQuoteArray();

      // Pickup / Drop dock (keep keys exactly as provided)
      const pickUpDock = {
        dockName: asStr(fields.PickUpDockName),
        pickupNumber: asStr(fields.PickUpDOckNumber),
        pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
        pickUpTime: fields.PickUpDockTime || "",
        note: asStr(fields.PickUpDOckNote),
        dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
        dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
        firstComeFirstServe: false,
      };

      const dropDock = {
        dockName: asStr(fields.DropDockName),
        pickupNumber: asStr(fields.DropDOckNumber), // key name matches your sample
        note: asStr(fields.DropDOckNote),
        dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
        dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
        firstComeFirstServe: false,
      };

      // Build final payload (mirror the sample 1:1 on keys)
      const payload = {
        groupId: asStr(fields.selectedQuoteId),
        // serviceId intentionally omitted (commented in sample)
        customerId,
        totalWeight:
          asNum(fields.destinationWeight) ||
          asNum(fields.destinationWeight) ||
          items.reduce((sum: number, it: any) => sum + asNum(it.weight), 0),
        distanceToDestination: asStr(fields.distanceToDestination),
        orderTypeId,
        isLtlOnly: isLtlOnly,
        reference: asStr(fields.orderReference),
        reference2: asStr(moreReferences?.[0]?.referenceValue),
        reference3: asStr(moreReferences?.[1]?.referenceValue),
        reference4: asStr(moreReferences?.[2]?.referenceValue),
        reference5: asStr(moreReferences?.[3]?.referenceValue),
        items,
        destinationAddress,
        originAddress,
        quoteArray,
        pickUpDock,
        dropDock,
        additionalServices: Array.isArray(fields.additionalServices)
          ? fields.additionalServices
          : [],
      };

      const apiResponse = await postApi(SAVE_QUOTATION_FOR_LTL, payload);
      if (apiResponse?.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (err) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (saveQuotationPage) {
      setTimeout(() => {
        setSaveQuotationPage(false);
      }, 3000);
    }
  }, [saveQuotationPage]);

  useEffect(() => {
    if (state?.page === "Quick Quote" && state?.isSelected) {
      setShowLTLList(true);
      setQuickQuoteLtlData(state?.selectedData);
      setQuickQuoteLtlFMData(state?.quoteData?.finalMileData);
      setLtlFMData(state?.quoteData);
      setPickupAccessorialsSelected(state?.data?.originAddress?.accessorials);
      setDropAccessorialsSelected(
        state?.ltlAccessorialData?.dropAccessorialsSelected
      );
      setPickupLimitedAccesssSelected(
        state?.ltlAccessorialData?.pickupLimitedAccessSelected
      );
      setDropLimitedAccesssSelected(
        state?.ltlAccessorialData?.dropLimitedAccessSelected
      );
      if (state?.selectedData?.orderTypeName === "LTL + Final Mile") {
        setOrderTypeRowData({
          id: ltlFMOrderType?.id,
          name: "LTL + Final Mile",
          description: "Less than Truck Load + Final Mile",
        });
        setLtlFMQuoteSelected(false);
      }
    }
  }, [state, pickupLimitedAccess?.length, dropLimitedAccess?.length]);

  // Create ltlDataToTransfer object similar to QuickQuote.tsx
  const ltlDataToTransfer = {
    quoteId: "",
    items: mapItemsDataToPayload(data),
    destinationAddress: {
      address: fields.destinationAddress,
      aptSuit: fields.destinationAPTSuite,
      zipCode: fields.destinationZipcode,
      city: fields.destinationCity,
      state: fields.destinationState,
      country: fields.destinationCountry,
      businessLocation: fields.destinationBusinessLocation,
      contactName: fields.destinationConsigneeName,
      phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
      countryCallingCode: fields.destinationConsigneePhoneExt,
      contactEmail: fields.destinationConsigneeContactEmail,
      extraMen: fields.destinationExtramen,
      waitTime: fields.destinationWaitTimeMinutes,
      stairsNumber: fields.destinationNoOfFlights,
      storage: fields.destinationStorage,
      handlingUnit: fields.destinationHandingUnits,
      weight: fields.destinationWeight,
      pieces: fields.destinationPieces,
      specialInstructions: fields.destinationSpecialInstructions,
    },
    originAddress: {
      address: fields.OriginAddress,
      aptSuit: fields.OriginAPTSuite,
      zipCode: fields.OriginZipcode,
      city: fields.OriginCity,
      state: fields.OriginState,
      country: fields.OriginCountry,
      businessLocation: fields.OriginBusinessLocation,
      contactName: fields.OriginConsigneeName,
      phoneNumber: fields.OriginConsigneePhoneNumber,
      countryCallingCode: fields.OriginConsigneePhoneExt,
      contactEmail: fields.OriginConsigneeContactEmail,
      accessorials: mapAccessorials(pickupAccessorialsSelected),
      locationType: pickupLimitedAccessSelected?.name || "",
    },

    customer: customerRowData,
    associatedCustomer: assCustomerRowData,
    service: serviceSelected,
    orderType: orderTypeRowData,
    reference: fields.orderReference,
    reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
    reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
    reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
    reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
    additionalServices: fields.additionalServices,
    distanceToDestination: fields.distance,
    totalWeight: fields.destinationWeight,

    pickUpDock: {
      dockName: fields.PickUpDockName || "",
      pickupNumber: fields.PickUpDOckNumber || "",
      pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
      pickUpTime: fields.PickUpDockTime || "",
      note: fields.PickUpDOckNote || "",
      dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
      dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
      firstComeFirstServe: fields.PickUpFCFS,
    },

    dropDock: {
      dockName: fields.DropDockName || "",
      pickupNumber: fields.DropDOckNumber || "",
      note: fields.DropDOckNote || "",
      dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
      dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
      firstComeFirstServe: fields.DropFCFS,
    },
  };

  return (
    <>
      <div ref={containerRef}>
        {loading && <Loader />}
        {/* <div className="details" id="top"> */}
        <div>
          <div className="form form--innersec">
            <section className="form__section">
              {!isUserOfOmniMove && customerLevel === "first" ? (
                <>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={customerNameDropdown}
                        setData={setCustomerNameDropdown}
                        captionTitles="Select Customer"
                        rowData={customerRowData}
                        setRowData={(row: any) => {
                          if (row?.id !== customerRowData?.id) {
                            resetCustomerLinkedAddresses();
                          }
                          setCustomerRowData(row);
                          clearLoadedOrderReference2();
                          clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.customer}
                        error={errors[DROPDOWN_FIELD_KEYS.customer]}
                        displayColumn="name"
                        apiUrl={"/customer"}
                        labelTitle="Customer Name"
                        mandatory={true}
                        enableRemove={true}
                        type={"order"}
                        status={3}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                        extraFields={["isUsedOrderReference2", "usedOrderReference2"]}
                      />
                    </div>
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={assCustomerNameDropdown}
                        setData={setAssCustomerNameDropdown}
                        captionTitles="Select Sub Customer"
                        rowData={
                          customerRowData?.customerLevel !== "second"
                            ? ""
                            : assCustomerRowData
                        }
                        setRowData={(row: any) => {
                          // Only a real selection counts as the user taking over:
                          // MutliDropdown also clears this row programmatically when
                          // parentCustomerId changes, which happens during a quote load.
                          if (row) {
                            if (row?.id !== assCustomerRowData?.id) {
                              resetCustomerLinkedAddresses();
                            }
                            clearLoadedOrderReference2();
                          }
                          setAssCustomerRowData(row);
                        }}
                        displayColumn="name"
                        enableRemove={true}
                        apiUrl={parentCustomerId ? "/customer" : ""}
                        labelTitle="Sub Customer Name"
                        mandatory={associatedCusReqired}
                        parentCustomerId={parentCustomerId}
                        disable={customerRowData?.customerLevel !== "second"}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                        extraFields={["isUsedOrderReference2", "usedOrderReference2"]}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={customerNameDropdown}
                        setData={setCustomerNameDropdown}
                        captionTitles="Select Customer"
                        rowData={customerRowData}
                        setRowData={(row: any) => {
                          if (row?.id !== customerRowData?.id) {
                            resetCustomerLinkedAddresses();
                          }
                          setCustomerRowData(row);
                          clearLoadedOrderReference2();
                          clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.customer}
                        error={errors[DROPDOWN_FIELD_KEYS.customer]}
                        displayColumn="name"
                        enableRemove={true}
                        apiUrl={"/customer"}
                        labelTitle="Customer Name"
                        mandatory={true}
                        type={"order"}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                        extraFields={["isUsedOrderReference2", "usedOrderReference2"]}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="formRow formRow__2 mb_0">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="orderReference" className="form__label">
                      Order Reference 1{" "}
                      <span className="mandatory-mark">*</span>
                    </label>
                    <input
                      type="text"
                      id="orderReference"
                      name="orderReference"
                      data-field="orderReference"
                      className={`form__input form__input--sm${fieldErrorClass("orderReference")}`}
                      placeholder="Order Reference"
                      autoComplete="off"
                      value={fields.orderReference}
                      onChange={handleFieldChange}
                    />
                    {errors.orderReference && (
                      <span className="form__error">{errors.orderReference}</span>
                    )}
                  </div>
                </div>
                {moreReferences.map((item, index) => {
                  // Reference 2 is driven by the customer's configuration when locked.
                  const isLocked =
                    isReference2Locked && index === ORDER_REFERENCE_2_INDEX;
                  return moreReferences.length < referenceLimit &&
                    index + 1 === moreReferences.length &&
                    moreReferences.length + 1 !== referenceLimit ? (
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="orderReference" className="form__label">
                          Order Reference {`${item?.referenceCount}`}
                        </label>
                        <div className={`form__groupField${isLocked ? " form__groupField--disabled" : ""}`}>
                          <input
                            type="text"
                            id="orderReference"
                            name={`orderReference${item?.referenceCount}`}
                            className="form__input form__input--sm"
                            placeholder="Order Reference"
                            autoComplete="off"
                            value={item.referenceValue}
                            disabled={isLocked}
                            onChange={(e) =>
                              handleRefFieldChange(e?.target?.value, index)
                            }
                          />
                          {moreReferences?.length > 1 && !isLocked && (
                            <button onClick={() => removeReferences(index)}>
                              <DeleteIcon />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="btn__group btn__group--end mt_8 mb_0">
                        <button
                          className={
                            moreReferences[moreReferences?.length - 1]
                              .referenceValue === ""
                              ? "addMore addMore__disable"
                              : "addMore"
                          }
                          disabled={
                            moreReferences[moreReferences?.length - 1]
                              .referenceValue === ""
                          }
                          onClick={() => addMoreReferences()}
                        >
                          +Add More References
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="orderReference" className="form__label">
                          Order Reference {`${item?.referenceCount}`}
                          {/* {item?.referenceCount === 2 ? (
                          <span className="mandatory-mark"> *</span>
                        ) : (
                          ""
                        )} */}
                        </label>
                        <div className={`form__groupField${isLocked ? " form__groupField--disabled" : ""}`}>
                          <input
                            type="text"
                            id="orderReference"
                            name={`orderReference${item?.referenceCount}`}
                            className="form__input form__input--sm"
                            placeholder="Order Reference"
                            autoComplete="off"
                            value={item.referenceValue}
                            disabled={isLocked}
                            onChange={(e) =>
                              handleRefFieldChange(e?.target?.value, index)
                            }
                          />
                          {!isLocked && (
                            <button
                              className="btn"
                              onClick={() => removeReferences(index)}
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            {!notLTL ? (
              <div className="form__sectionGrid">
                <section className="form__section">
                  <h1 className="form__section__title">Origin Address</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      {/* <div className="form__Field">
                      <label htmlFor="OriginAddress" className="form__label">
                        Address <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="OriginAddress"
                        name="OriginAddress"
                        data-field="OriginAddress"
                        className={`form__input form__input--sm${fieldErrorClass("OriginAddress")}`}
                        placeholder="Address"
                        autoComplete="off"
                        value={fields.OriginAddress}
                        onChange={handleFieldChange}
                      />
                      {errors.OriginAddress && (
                        <span className="form__error">
                          {errors.OriginAddress}
                        </span>
                      )}
                    </div> */}
                      <div className="form__Field">
                        <label htmlFor="serviceLevel" className="form__label">
                          Address <span className="mandatory-mark">*</span>
                        </label>
                        <AddressDropDown
                          fieldKey="OriginAddress"
                          error={errors.OriginAddress}
                          columns={addressColumns}
                          data={originAddressList}
                          setData={setOriginAddressList}
                          captionTitles="Address"
                          rowData={addressSelectedOrigin}
                          setRowData={(full) => {
                            // full is the complete object from your backend OR a manual entry like:
                            // { address: "typed text", isManual: true }
                            setAddressSelectedOrigin(full);

                            // Always update these
                            const updatedFields: any = {
                              OriginAddress: full?.address || "",
                              OriginAddressId: full?.id || "",
                            };

                            // Only hydrate the remaining fields if this is a REAL selection (not free-text)
                            if (!full?.isManual) {
                              updatedFields.OriginAPTSuite =
                                full?.aptSuit || "";
                              updatedFields.OriginZipcode = full?.zipCode || "";
                              updatedFields.OriginCity = full?.city || "";
                              updatedFields.OriginState = full?.state || "";
                              updatedFields.OriginCountry = full?.country || "";
                              updatedFields.OriginBusinessLocation =
                                full?.businessLocation || "";
                              updatedFields.OriginConsigneeName =
                                full?.contactPerson || "";
                              updatedFields.OriginConsigneePhoneNumber =
                                full?.phoneNumber || "";
                              updatedFields.OriginConsigneePhoneExt =
                                full?.countryCallingCode || "+1";
                              updatedFields.OriginConsigneeContactEmail =
                                Array.isArray(full?.contactEmail)
                                  ? full?.contactEmail[0] || ""
                                  : full?.contactEmail || "";
                              // Pickup dock window saved against this address
                              updatedFields.PickUpDockOpenTime =
                                storedTimeToPicker(full?.pickupDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                              updatedFields.PickUpDOckCloseTime =
                                storedTimeToPicker(full?.pickupDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                            }
                            // If isManual === true, we intentionally DO NOT touch the other fields,
                            // so they keep their previous values instead of becoming "".

                            setFields((prev) => ({
                              ...prev,
                              ...updatedFields,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              OriginConsigneePhoneNumber: "",
                            }));
                          }}
                          displayColumn="address"
                          apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                          type="dropdown"
                          mandatory={true}
                          overwriteWithFreeText={true}
                          customerId={assCustomerRowData?.id || customerRowData?.id}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="OriginAPTSuite" className="form__label">
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="OriginAPTSuite"
                          name="OriginAPTSuite"
                          className="form__input form__input--sm"
                          autoComplete="off"
                          placeholder="APT/Suite"
                          value={fields.OriginAPTSuite}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2 formRowMFull">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="OriginZipcode" className="form__label">
                          Zip Code <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="OriginZipcode"
                          name="OriginZipcode"
                          data-field="OriginZipcode"
                          className={`form__input form__input--sm${fieldErrorClass("OriginZipcode")}`}
                          placeholder="Zip Code"
                          autoComplete="off"
                          value={fields.OriginZipcode}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginZipcode && (
                          <span className="form__error">
                            {errors.OriginZipcode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label htmlFor="OriginCity" className="form__label">
                              City
                            </label>
                            {originCities.length > 0 ? (
                              <SingleDropdown
                                fieldName="OriginCity"
                                selectedItem={fields.OriginCity ? { id: fields.OriginCity, name: fields.OriginCity } : null}
                                setSelectedData={(_, item) => setFields((prev) => ({ ...prev, OriginCity: item?.name || "" }))}
                                staticData={originCities.map((c) => ({ id: c, name: c }))}
                                placeholder="City"
                              />
                            ) : (
                              <input
                                type="text"
                                id="OriginCity"
                                className="form__input form__input--sm form__input--disabled"
                                placeholder="City"
                                autoComplete="off"
                                value={fields.OriginCity}
                                onChange={handleFieldChange}
                                disabled={true}
                              />
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="OriginState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="OriginState"
                              name="OriginState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={fields.OriginState}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="OriginCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="OriginCountry"
                              name="OriginCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={fields.OriginCountry}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginBusinessLocation"
                          className="form__label"
                        >
                          Business Location Name
                          {!notLTL && (
                            <span className="mandatory-mark"> *</span>
                          )}
                        </label>
                        <input
                          type="text"
                          id="OriginBusinessLocation"
                          name="OriginBusinessLocation"
                          data-field="OriginBusinessLocation"
                          autoComplete="off"
                          className={`form__input form__input--sm${fieldErrorClass("OriginBusinessLocation")}`}
                          placeholder="Business Location Name"
                          value={fields.OriginBusinessLocation}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginBusinessLocation && (
                          <span className="form__error">{errors.OriginBusinessLocation}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginConsigneeName"
                          className="form__label"
                        >
                          Contact Name <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="OriginConsigneeName"
                          name="OriginConsigneeName"
                          data-field="OriginConsigneeName"
                          className={`form__input form__input--sm${fieldErrorClass("OriginConsigneeName")}`}
                          placeholder="Contact Name"
                          autoComplete="off"
                          value={fields.OriginConsigneeName}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginConsigneeName && (
                          <span className="form__error">{errors.OriginConsigneeName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginConsigneePhoneNumber"
                          className="form__label"
                        >
                          Phone Number <span className="mandatory-mark">*</span>
                        </label>
                        <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("OriginConsigneePhoneNumber")}`}>
                          <select
                            className="form__select form__select--sm"
                            value={fields.OriginConsigneePhoneExt}
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                OriginConsigneePhoneExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={item?.countryCallingCode}
                              >
                                {item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            ref={originPhoneRef}
                            type="text"
                            id="OriginConsigneePhoneNumber"
                            name="OriginConsigneePhoneNumber"
                            data-field="OriginConsigneePhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Phone Number"
                            maxLength={
                              fields?.OriginConsigneePhoneExt === "+1" ? 16 : 10
                            }
                            value={onInputPhNumFormat(
                              fields.OriginConsigneePhoneNumber,
                              fields.OriginConsigneePhoneExt
                            )}
                            autoComplete="off"
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            onBlur={() =>
                              handlePhoneBlur(
                                fields.OriginConsigneePhoneNumber,
                                fields.OriginConsigneePhoneExt,
                                "OriginConsigneePhoneNumber"
                              )
                            }
                          />
                        </div>
                        {errors.OriginConsigneePhoneNumber && (
                          <span className="form__error">
                            {errors.OriginConsigneePhoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="email" className="form__label">
                          Contact Email{" "}
                          {/* {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )} */}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="OriginConsigneeContactEmail"
                          className={`form__input form__input--sm`}
                          placeholder="Contact Email"
                          autoComplete="off"
                          value={fields.OriginConsigneeContactEmail}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginConsigneeContactEmail && (
                          <span className="form__error">
                            {errors.OriginConsigneeContactEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <MultiSelectChips
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            dropdownData={pickupAccessorials}
                            selectedData={pickupAccessorialsSelected}
                            setDropdownData={setPickupAccessorialDropdownData}
                            setSelectedData={setPickupAccessorialsSelected}
                            searchPlaceholder="Search"
                            extendedUrl={{ isPickup: "true" }}
                            labelTitle="LTL Pickup Accessorials"
                            keyBindName="name"
                            mandatory={false}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <MutliDropdown
                            columns={accessorialsColumn}
                            data={pickupLimitedAccess}
                            setData={setPickupLimitedAccessDropdownData}
                            captionTitles="Select Limited Access Type"
                            rowData={pickupLimitedAccessSelected}
                            setRowData={setPickupLimitedAccesssSelected}
                            displayColumn="name"
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            labelTitle="Limited Access Type"
                            extendedUrl={{
                              isPickup: "true",
                              isLimitAccess: "true",
                            }}
                            enableRemove={true}
                            mandatory={false}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </section>
                <section className="form__section">
                  <h1 className="form__section__title">Destination Address</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      {/* <div className="form__Field">
                      <label
                        htmlFor="destinationAddress"
                        className="form__label"
                      >
                        Address <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationAddress"
                        name="destinationAddress"
                        data-field="destinationAddress"
                        className={`form__input form__input--sm${fieldErrorClass("destinationAddress")}`}
                        placeholder="Address"
                        autoComplete="off"
                        value={fields.destinationAddress}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationAddress && (
                        <span className="form__error">
                          {errors.destinationAddress}
                        </span>
                      )}
                    </div> */}
                      <div className="form__Field">
                        <label htmlFor="serviceLevel" className="form__label">
                          Address <span className="mandatory-mark">*</span>
                        </label>
                        <AddressDropDown
                          fieldKey="destinationAddress"
                          error={errors.destinationAddress}
                          columns={addressColumns}
                          data={destAddressList}
                          setData={setDestAddressList}
                          captionTitles="Address"
                          rowData={addressSelected}
                          setRowData={(full) => {
                            // Save the selected/manual entry
                            setAddressSelected(full);

                            // Always update the address text and addressId
                            const updatedFields: any = {
                              destinationAddress: full?.address || "",
                              destinationAddressId: full?.id || "",
                            };

                            // Only hydrate the rest if it's a real selection (not manual free-text)
                            if (!full?.isManual) {
                              updatedFields.destinationAPTSuite =
                                full?.aptSuit || "";
                              updatedFields.destinationZipcode =
                                full?.zipCode || "";
                              updatedFields.destinationCity = full?.city || "";
                              updatedFields.destinationState =
                                full?.state || "";
                              updatedFields.destinationCountry =
                                full?.country || "";
                              updatedFields.destinationBusinessLocation =
                                full?.businessLocation || "";
                              updatedFields.destinationConsigneeName =
                                full?.contactPerson || "";
                              updatedFields.destinationConsigneePhoneNumber =
                                full?.phoneNumber || "";
                              updatedFields.destinationConsigneePhoneExt =
                                full?.countryCallingCode || "+1";
                              updatedFields.destinationConsigneeContactEmail =
                                Array.isArray(full?.contactEmail)
                                  ? full?.contactEmail[0] || ""
                                  : full?.contactEmail || "";
                              // Drop dock window saved against this address. LTL + Final Mile
                              // forces this window to 00:00 and disables it, so skip it there.
                              if (
                                orderTypeRowData?.name !== "LTL + Final Mile"
                              ) {
                                updatedFields.DropDockOpenTime =
                                  storedTimeToPicker(full?.dropDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                                updatedFields.DropDOckCloseTime =
                                  storedTimeToPicker(full?.dropDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                                dropHoursAreDefaultRef.current = {
                                  open: !storedTimeToPicker(full?.dropDockOpenTime),
                                  close: !storedTimeToPicker(full?.dropDockCloseTime),
                                };
                              }
                            }
                            // If isManual, we intentionally leave other fields untouched.

                            setFields((prev) => ({
                              ...prev,
                              ...updatedFields,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              destinationConsigneePhoneNumber: "",
                            }));
                          }}
                          displayColumn="address"
                          apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                          type="dropdown"
                          mandatory={true}
                          overwriteWithFreeText={true}
                          customerId={assCustomerRowData?.id || customerRowData?.id}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationAPTSuite"
                          className="form__label"
                        >
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="destinationAPTSuite"
                          name="destinationAPTSuite"
                          className="form__input form__input--sm"
                          placeholder="APT/Suite"
                          autoComplete="off"
                          value={fields.destinationAPTSuite}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2 formRowMFull">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationZipcode"
                          className="form__label"
                        >
                          Zip Code <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="destinationZipcode"
                          name="destinationZipcode"
                          data-field="destinationZipcode"
                          className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                          placeholder="Zip Code"
                          autoComplete="off"
                          value={fields.destinationZipcode}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationZipcode && (
                          <span className="form__error">
                            {errors.destinationZipcode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationCity"
                              className="form__label"
                            >
                              City
                            </label>
                            {destinationCities.length > 0 ? (
                              <SingleDropdown
                                fieldName="destinationCity"
                                selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                                setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                                staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                                placeholder="City"
                              />
                            ) : (
                              <input
                                type="text"
                                id="destinationCity"
                                className="form__input form__input--sm form__input--disabled"
                                placeholder="City"
                                autoComplete="off"
                                value={fields.destinationCity}
                                onChange={handleFieldChange}
                                disabled={true}
                              />
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="destinationState"
                              name="destinationState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={fields.destinationState}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="destinationCountry"
                              name="destinationCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={fields.destinationCountry}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationBusinessLocation"
                          className="form__label"
                        >
                          Business Location {!notLTL && "Name "}
                          {!notLTL && <span className="mandatory-mark">*</span>}
                        </label>
                        <input
                          type="text"
                          id="destinationBusinessLocation"
                          name="destinationBusinessLocation"
                          data-field="destinationBusinessLocation"
                          autoComplete="off"
                          className={`form__input form__input--sm${fieldErrorClass("destinationBusinessLocation")}`}
                          placeholder={
                            notLTL
                              ? "Business Location"
                              : "Business Location Name"
                          }
                          value={fields.destinationBusinessLocation}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationBusinessLocation && (
                          <span className="form__error">{errors.destinationBusinessLocation}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneeName"
                          className="form__label"
                        >
                          Consignee Name{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="destinationConsigneeName"
                          name="destinationConsigneeName"
                          data-field="destinationConsigneeName"
                          className={`form__input form__input--sm${fieldErrorClass("destinationConsigneeName")}`}
                          placeholder="Consignee Name"
                          autoComplete="off"
                          value={fields.destinationConsigneeName}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationConsigneeName && (
                          <span className="form__error">{errors.destinationConsigneeName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneePhoneNumber"
                          className="form__label"
                        >
                          Consignee Phone Number{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("destinationConsigneePhoneNumber")}`}>
                          <select
                            className="form__select form__select--sm"
                            value={fields.destinationConsigneePhoneExt}
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                destinationConsigneePhoneExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={item?.countryCallingCode}
                              >
                                {item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            ref={destinationPhoneRef}
                            type="text"
                            id="destinationConsigneePhoneNumber"
                            name="destinationConsigneePhoneNumber"
                            data-field="destinationConsigneePhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Consignee Phone Number"
                            maxLength={
                              fields?.destinationConsigneePhoneExt === "+1"
                                ? 16
                                : 10
                            }
                            value={onInputPhNumFormat(
                              fields.destinationConsigneePhoneNumber,
                              fields.destinationConsigneePhoneExt
                            )}
                            autoComplete="off"
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            onPasteCapture={handleFieldChange}
                            onBlur={() =>
                              handlePhoneBlur(
                                fields.destinationConsigneePhoneNumber,
                                fields.destinationConsigneePhoneExt,
                                "destinationConsigneePhoneNumber"
                              )
                            }
                          />
                        </div>
                        {errors.destinationConsigneePhoneNumber && (
                          <span className="form__error">
                            {errors.destinationConsigneePhoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="email" className="form__label">
                          Consignee Contact Email{" "}
                          {/* {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )} */}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="destinationConsigneeContactEmail"
                          className={`form__input form__input--sm`}
                          placeholder="Consignee Contact Email"
                          autoComplete="off"
                          value={fields.destinationConsigneeContactEmail}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationConsigneeContactEmail && (
                          <span className="form__error">
                            {errors.destinationConsigneeContactEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationHandingUnits"
                            className="form__label"
                          >
                            Handling Units
                          </label>
                          <input
                            type="number"
                            id="destinationHandingUnits"
                            name="destinationHandingUnits"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Handling Units"
                            autoComplete="off"
                            value={fields.destinationHandingUnits}
                            onChange={handleFieldChange}
                            disabled={true}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationWeight"
                            className="form__label"
                          >
                            Total Weight(LBS)
                          </label>
                          <input
                            type="number"
                            id="destinationWeight"
                            name="destinationWeight"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Total Weight"
                            autoComplete="off"
                            value={fields.destinationWeight}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationPieces"
                            className="form__label"
                          >
                            Total Items/Pieces
                          </label>
                          <input
                            type="number"
                            id="destinationPieces"
                            name="destinationPieces"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Pieces"
                            autoComplete="off"
                            value={fields.destinationPieces}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* </div> */}
                  {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                    <div className="formRow formRow__2 mb_0">
                      <div className="formCol">
                        <div className="form__Field">
                          <MultiSelectChips
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            dropdownData={dropAccessorials}
                            selectedData={dropAccessorialsSelected}
                            setDropdownData={setDropAccessorialDropdownData}
                            setSelectedData={setDropAccessorialsSelected}
                            searchPlaceholder="Search"
                            extendedUrl={{ isPickup: "false" }} // Delivery
                            labelTitle="LTL Delivery Accessorials"
                            keyBindName="name"
                            mandatory={false}
                            // disable={orderTypeRowData?.name === "LTL + Final Mile"}
                            callingFrom="TraditionalQuote"
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <MutliDropdown
                            columns={accessorialsColumn}
                            data={dropLimitedAccess}
                            setData={setDropLimitedAccessDropdownData}
                            captionTitles="Select Limited Access Type"
                            rowData={dropLimitedAccessSelected}
                            setRowData={setDropLimitedAccesssSelected}
                            displayColumn="name"
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            extendedUrl={{
                              isPickup: "false",
                              isLimitAccess: "true",
                            }}
                            labelTitle="Limited Access Type"
                            enableRemove={true}
                            mandatory={false}
                          // disable={
                          //   orderTypeRowData?.name === "LTL + Final Mile"
                          // }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(notLTL || orderTypeRowData?.name === "Expedited") && (
                    <>
                      <div className="formRow mb_0">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationSpecialInstructions"
                              className="form__label"
                            >
                              Special Instructions
                            </label>
                            <textarea
                              id="destinationSpecialInstructions"
                              name="destinationSpecialInstructions"
                              className="form__textarea form__textarea--xs"
                              placeholder="Special Instructions"
                              autoComplete="off"
                              value={fields.destinationSpecialInstructions}
                              onChange={handleTextAreaChange}
                              maxLength={255}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      {/* <div className="formRow formRow--gapNone">
                      <h1 className="form__section__title form__section__title--flex">
                        Add Item Details
                        <div className="btn__group">
                          <button
                            className="btn btn__white"
                            onClick={handleAddItemDetails}
                          >
                            <AddIcon />
                            Add New Item
                          </button>
                        </div>
                      </h1>
                      <div className="tableWrap tableWrap--order p_0">
                        <ItemDetailsTable
                          invalidCells={invalidCells}
                          fieldKey={HANDLING_UNITS_FIELD_KEY}
                          focusCell={focusCell}
                          columns={
                            orderTypeRowData?.name === "Expedited"
                              ? tableConfig.columnsExpediet
                              : tableConfig.columns
                          }
                          data={data}
                          setData={setData}
                          setLoading={setLoading}
                          onDeleteConfig={tableConfig.delete}
                          showDelete={true}
                          isEditable={true}
                          setFields={setFields}
                          isLTL={true}
                        />
                      </div>
                    </div> */}
                    </>
                  )}
                </section>
              </div>
            ) : (
              <section className="form__section">
                <h1 className="form__section__title">Destination Address</h1>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    {/* <div className="form__Field">
                    <label htmlFor="destinationAddress" className="form__label">
                      Address <span className="mandatory-mark">*</span>
                    </label>
                    <input
                      type="text"
                      id="destinationAddress"
                      name="destinationAddress"
                      data-field="destinationAddress"
                      className={`form__input form__input--sm${fieldErrorClass("destinationAddress")}`}
                      placeholder="Address"
                      autoComplete="off"
                      value={fields.destinationAddress}
                      onChange={handleFieldChange}
                    />
                    {errors.destinationAddress && (
                      <span className="form__error">
                        {errors.destinationAddress}
                      </span>
                    )}
                  </div> */}
                    <div className="form__Field">
                      <label htmlFor="serviceLevel" className="form__label">
                        Address <span className="mandatory-mark">*</span>
                      </label>
                      <AddressDropDown
                        fieldKey="destinationAddress"
                        error={errors.destinationAddress}
                        columns={addressColumns}
                        data={destAddressList}
                        setData={setDestAddressList}
                        captionTitles="Address"
                        rowData={addressSelected}
                        setRowData={(full) => {
                          // Store selection/manual entry
                          setAddressSelected(full);

                          // Always update the visible address and (optionally) the ID
                          const updatedFields: any = {
                            destinationAddress: full?.address || "",
                            destinationAddressId: full?.id || "",
                          };

                          // Only hydrate the rest if this is NOT a manual free-text entry
                          if (!full?.isManual) {
                            updatedFields.destinationAPTSuite =
                              full?.aptSuit || "";
                            updatedFields.destinationZipcode =
                              full?.zipCode || "";
                            updatedFields.destinationCity = full?.city || "";
                            updatedFields.destinationState = full?.state || "";
                            updatedFields.destinationCountry =
                              full?.country || "";
                            updatedFields.destinationBusinessLocation =
                              full?.businessLocation || "";
                            updatedFields.destinationConsigneeName =
                              full?.contactPerson || "";
                            updatedFields.destinationConsigneePhoneNumber =
                              full?.phoneNumber || "";
                            updatedFields.destinationConsigneePhoneExt =
                              full?.countryCallingCode || "+1";
                            updatedFields.destinationConsigneeContactEmail =
                              Array.isArray(full?.contactEmail)
                                ? full?.contactEmail[0] || ""
                                : full?.contactEmail || "";
                            // Drop dock window saved against this address. LTL + Final Mile
                            // forces this window to 00:00 and disables it, so skip it there.
                            if (orderTypeRowData?.name !== "LTL + Final Mile") {
                              updatedFields.DropDockOpenTime =
                                storedTimeToPicker(full?.dropDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                              updatedFields.DropDOckCloseTime =
                                storedTimeToPicker(full?.dropDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                              dropHoursAreDefaultRef.current = {
                                open: !storedTimeToPicker(full?.dropDockOpenTime),
                                close: !storedTimeToPicker(full?.dropDockCloseTime),
                              };
                            }
                          }
                          // If isManual, leave other destination* fields untouched.

                          setFields((prev) => ({
                            ...prev,
                            ...updatedFields,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            destinationConsigneePhoneNumber: "",
                          }));
                        }}
                        displayColumn="address"
                        apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                        type="dropdown"
                        mandatory={true}
                        overwriteWithFreeText={true}
                        customerId={assCustomerRowData?.id || customerRowData?.id}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationAPTSuite"
                        className="form__label"
                      >
                        APT / Suite
                      </label>
                      <input
                        type="text"
                        id="destinationAPTSuite"
                        name="destinationAPTSuite"
                        className="form__input form__input--sm"
                        placeholder="APT/Suite"
                        autoComplete="off"
                        value={fields.destinationAPTSuite}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationZipcode"
                        className="form__label"
                      >
                        Zip Code <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationZipcode"
                        name="destinationZipcode"
                        data-field="destinationZipcode"
                        className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                        placeholder="Zip Code"
                        autoComplete="off"
                        value={fields.destinationZipcode}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationZipcode && (
                        <span className="form__error">
                          {errors.destinationZipcode}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCity"
                            className="form__label"
                          >
                            City
                          </label>
                          {destinationCities.length > 0 ? (
                            <SingleDropdown
                              fieldName="destinationCity"
                              selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                              setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                              staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                              placeholder="City"
                            />
                          ) : (
                            <input
                              type="text"
                              id="destinationCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={fields.destinationCity}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          )}
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationState"
                            className="form__label"
                          >
                            State
                          </label>
                          <input
                            type="text"
                            id="destinationState"
                            name="destinationState"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="State"
                            autoComplete="off"
                            value={fields.destinationState}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCountry"
                            className="form__label"
                          >
                            Country
                          </label>
                          <input
                            type="text"
                            id="destinationCountry"
                            name="destinationCountry"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Country"
                            autoComplete="off"
                            value={fields.destinationCountry}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationBusinessLocation"
                        className="form__label"
                      >
                        Business Location {!notLTL && "Name "}
                        {!notLTL && <span className="mandatory-mark">*</span>}
                      </label>
                      <input
                        type="text"
                        id="destinationBusinessLocation"
                        name="destinationBusinessLocation"
                        data-field="destinationBusinessLocation"
                        className={`form__input form__input--sm${fieldErrorClass("destinationBusinessLocation")}`}
                        autoComplete="off"
                        placeholder={
                          notLTL
                            ? "Business Location"
                            : "Business Location Name"
                        }
                        value={fields.destinationBusinessLocation}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationBusinessLocation && (
                        <span className="form__error">{errors.destinationBusinessLocation}</span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneeName"
                        className="form__label"
                      >
                        Consignee Name <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationConsigneeName"
                        name="destinationConsigneeName"
                        data-field="destinationConsigneeName"
                        className={`form__input form__input--sm${fieldErrorClass("destinationConsigneeName")}`}
                        placeholder="Consignee Name"
                        autoComplete="off"
                        value={fields.destinationConsigneeName}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationConsigneeName && (
                        <span className="form__error">{errors.destinationConsigneeName}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Consignee Phone Number{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("destinationConsigneePhoneNumber")}`}>
                        <select
                          className="form__select form__select--sm"
                          value={fields.destinationConsigneePhoneExt}
                          onChange={(e) => {
                            setFields(() => ({
                              ...fields,
                              destinationConsigneePhoneExt: e.target.value,
                            }));
                          }}
                        >
                          {countryCode?.map((item: any) => (
                            <option
                              key={item?.countryCallingCode}
                              value={item?.countryCallingCode}
                            >
                              {item?.countryCallingCode}
                            </option>
                          ))}
                        </select>
                        <input
                          ref={destinationPhoneRef}
                          type="text"
                          id="destinationConsigneePhoneNumber"
                          name="destinationConsigneePhoneNumber"
                          data-field="destinationConsigneePhoneNumber"
                          className="form__input form__input--sm"
                          placeholder="Consignee Phone Number"
                          maxLength={
                            fields?.destinationConsigneePhoneExt === "+1"
                              ? 16
                              : 10
                          }
                          value={onInputPhNumFormat(
                            fields.destinationConsigneePhoneNumber,
                            fields.destinationConsigneePhoneExt
                          )}
                          onChange={handleFieldChange}
                          onSelect={handleFieldChange}
                          autoComplete="off"
                          onBlur={() =>
                            handlePhoneBlur(
                              fields.destinationConsigneePhoneNumber,
                              fields.destinationConsigneePhoneExt,
                              "destinationConsigneePhoneNumber"
                            )
                          }
                        />
                      </div>
                      {errors.destinationConsigneePhoneNumber && (
                        <span className="form__error">
                          {errors.destinationConsigneePhoneNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="email" className="form__label">
                        Consignee Contact Email{" "}
                        {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="destinationConsigneeContactEmail"
                        className={`form__input form__input--sm`}
                        placeholder="Consignee Contact Email"
                        autoComplete="off"
                        value={fields.destinationConsigneeContactEmail}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationConsigneeContactEmail && (
                        <span className="form__error">
                          {errors.destinationConsigneeContactEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="formRow">
                  {(notLTL ||
                    orderTypeRowData?.name === "Expedited" ||
                    orderTypeRowData?.name === "LTL + Final Mile") && (
                      <div className="formCol">
                        <div className="formRow formRow__3">
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationExtramen"
                                className="form__label"
                              >
                                Extra Men
                              </label>
                              <input
                                type="number"
                                id="destinationExtramen"
                                name="destinationExtramen"
                                className="form__input form__input--sm"
                                placeholder="Extra Men"
                                autoComplete="off"
                                value={
                                  fields.destinationExtramen !== ""
                                    ? Math.abs(Number(fields.destinationExtramen))
                                    : fields.destinationExtramen
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationNoOfFlights"
                                className="form__label"
                              >
                                No. of Flights (Stairs)
                              </label>
                              <input
                                type="number"
                                id="destinationNoOfFlights"
                                name="destinationNoOfFlights"
                                className="form__input form__input--sm"
                                placeholder="No. of Flights"
                                autoComplete="off"
                                value={
                                  fields.destinationNoOfFlights !== ""
                                    ? Math.abs(
                                      Number(fields.destinationNoOfFlights)
                                    )
                                    : fields.destinationNoOfFlights
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationWaitTimeMinutes"
                                className="form__label"
                              >
                                Wait Time (Minutes)
                              </label>
                              <input
                                type="number"
                                id="destinationWaitTimeMinutes"
                                name="destinationWaitTimeMinutes"
                                className="form__input form__input--sm"
                                placeholder="Wait Time"
                                autoComplete="off"
                                value={
                                  fields.destinationWaitTimeMinutes !== ""
                                    ? Math.abs(
                                      Number(fields.destinationWaitTimeMinutes)
                                    )
                                    : fields.destinationWaitTimeMinutes
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          {/* <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationStorage"
                            className="form__label"
                          >
                            Storage
                          </label>
                          <input
                            type="number"
                            id="destinationStorage"
                            name="destinationStorage"
                            className="form__input form__input--sm"
                            placeholder="Storage"
                            value={fields.destinationStorage}
                            onChange={handleFieldChange}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div> */}
                        </div>
                      </div>
                    )}
                </div>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationHandingUnits"
                        className="form__label"
                      >
                        Handling Units
                      </label>
                      <input
                        type="number"
                        id="destinationHandingUnits"
                        name="destinationHandingUnits"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Handling Units"
                        autoComplete="off"
                        value={fields.destinationHandingUnits}
                        onChange={handleFieldChange}
                        disabled={true}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationWeight"
                        className="form__label"
                      >
                        Total Weight(LBS)
                      </label>
                      <input
                        type="number"
                        id="destinationWeight"
                        name="destinationWeight"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Total Weight"
                        autoComplete="off"
                        value={fields.destinationWeight}
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationPieces"
                        className="form__label"
                      >
                        Total Items/Pieces
                      </label>
                      <input
                        type="number"
                        id="destinationPieces"
                        name="destinationPieces"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Pieces"
                        autoComplete="off"
                        value={fields.destinationPieces}
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>

                {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                  <div className="formRow formRow__2 mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <MultiSelectChips
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          dropdownData={dropAccessorials}
                          selectedData={dropAccessorialsSelected}
                          setDropdownData={setDropAccessorialDropdownData}
                          setSelectedData={setDropAccessorialsSelected}
                          searchPlaceholder="Search"
                          extendedUrl={{ isPickup: "false" }} // Delivery
                          labelTitle="LTL Delivery Accessorials"
                          keyBindName="name"
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          callingFrom="TraditionalQuote" // ← enables the 3 extra items
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <MutliDropdown
                          columns={accessorialsColumn}
                          data={dropLimitedAccess}
                          setData={setDropLimitedAccessDropdownData}
                          captionTitles="Select Limited Access Type"
                          rowData={dropLimitedAccessSelected}
                          setRowData={setDropLimitedAccesssSelected}
                          displayColumn="name"
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          extendedUrl={{
                            isPickup: "false",
                            isLimitAccess: "true",
                          }}
                          labelTitle="Limited Access Type"
                          enableRemove={true}
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}
            {notLTL === false && orderTypeRowData?.name !== "Expedited" ? (
              <div className="form__sectionGrid">
                <section className="form__section">
                  <h1 className="form__section__title">Pickup Dock Data</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="PickUpDockName" className="form__label">
                          Dock Name
                        </label>
                        <input
                          type="text"
                          id="PickUpDockName"
                          name="PickUpDockName"
                          className="form__input form__input--sm"
                          placeholder="Dock Name"
                          autoComplete="off"
                          value={fields.PickUpDockName}
                          onChange={handleFieldChange}
                        />
                        {errors.PickUpDockName && (
                          <span className="form__error">
                            {errors.PickUpDockName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDOckNumber"
                          className="form__label"
                        >
                          Pickup Number
                        </label>
                        <input
                          type="text"
                          id="PickUpDOckNumber"
                          name="PickUpDOckNumber"
                          className="form__input form__input--sm"
                          placeholder="Pickup Number"
                          autoComplete="off"
                          value={fields.PickUpDOckNumber}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Pickup Date <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="date"
                          id="PickUpDockDate"
                          name="PickUpDockDate"
                          data-field="PickUpDockDate"
                          className={`form__date form__date--sm${fieldErrorClass("PickUpDockDate")}`}
                          placeholder="DD/MM/YYYY"
                          autoComplete="off"
                          min={moment().format("YYYY-MM-DD")}
                          onChange={handleFieldChange}
                          value={
                            normalizeDate(fields.PickUpDockDate) ||
                            state?.data?.PickUpDockDate
                          }
                        />
                      </div>
                      {errors.PickUpDockDate && (
                        <span className="form__error">
                          {errors.PickUpDockDate}
                        </span>
                      )}
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="PickUpDockTime" className="form__label">
                          Pickup Time <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            fields.PickUpDockTime || state?.data?.PickUpDockTime
                          }
                          onChange={handleTimeChange}
                          property="PickUpDockTime"
                          fieldKey="PickUpDockTime"
                          error={errors.PickUpDockTime}
                        />
                      </div>
                      {errors.PickUpDockTime && (
                        <span className="form__error">
                          {errors.PickUpDockTime}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDockOpenTime"
                          className="form__label"
                        >
                          Dock Open Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={fields.PickUpDockOpenTime}
                          onChange={handleTimeChange}
                          property="PickUpDockOpenTime"
                          fieldKey="PickUpDockOpenTime"
                          error={errors.PickUpDockOpenTime}
                        />
                        {errors.PickUpDockOpenTime && (
                          <span className="form__error">{errors.PickUpDockOpenTime}</span>
                        )}
                      </div>
                      <div
                        className="form__checkbox mt_12"
                        style={{ width: "max-content" }}
                      >
                        <div className="form__checkbox__option mb_0">
                          <input
                            type="checkbox"
                            id="PickUpFCFS"
                            className={`form__checkbox__input `}
                            checked={fields.PickUpFCFS}
                            onChange={handleCheckboxChange}
                          />
                          <label className="form__checkbox__label">
                            First come first serve
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDOckCloseTime"
                          className="form__label"
                        >
                          Dock Close Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={fields.PickUpDOckCloseTime}
                          onChange={handleTimeChange}
                          property="PickUpDOckCloseTime"
                          fieldKey="PickUpDOckCloseTime"
                          error={errors.PickUpDOckCloseTime}
                        />
                        {errors.PickUpDOckCloseTime && (
                          <span className="form__error">{errors.PickUpDOckCloseTime}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Note</label>
                        <textarea
                          id="PickUpDOckNote"
                          name="PickUpDOckNote"
                          className="form__textarea form__textarea--xs"
                          placeholder="Special Instructions"
                          autoComplete="off"
                          value={fields.PickUpDOckNote}
                          onChange={handleTextAreaChange}
                          maxLength={255}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="form__section">
                  <h1 className="form__section__title">Drop Dock Data</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="DropDockName" className="form__label">
                          Dock Name
                        </label>
                        <input
                          type="text"
                          id="DropDockName"
                          name="DropDockName"
                          className={`form__input form__input--sm ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__input--disabled"
                            : ""
                            }`}
                          placeholder="Dock Name"
                          autoComplete="off"
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDockName
                          }
                          onChange={handleFieldChange}
                        />
                        {errors.DropDockName && (
                          <span className="form__error">
                            {errors.DropDockName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="DropDOckNumber" className="form__label">
                          Pickup Number
                        </label>
                        <input
                          type="text"
                          id="DropDOckNumber"
                          name="DropDOckNumber"
                          className={`form__input form__input--sm ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__input--disabled"
                            : ""
                            }`}
                          placeholder="Pickup Number"
                          autoComplete="off"
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckNumber
                          }
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="waittime" className="form__label">
                          Dock Open Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDockOpenTime
                          }
                          onChange={handleTimeChange}
                          property="DropDockOpenTime"
                          fieldKey="DropDockOpenTime"
                          error={errors.DropDockOpenTime}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? true
                              : false
                          }
                        />
                        {errors.DropDockOpenTime && (
                          <span className="form__error">{errors.DropDockOpenTime}</span>
                        )}
                      </div>
                      <div
                        className="form__checkbox mt_12"
                        style={{ width: "max-content" }}
                      >
                        <div className="form__checkbox__option mb_0">
                          <input
                            type="checkbox"
                            id="DropFCFS"
                            className={`form__checkbox__input `}
                            checked={fields.DropFCFS}
                            disabled={
                              orderTypeRowData?.name === "LTL + Final Mile"
                            }
                            autoComplete="off"
                            onChange={handleCheckboxChange}
                          />
                          <label className="form__checkbox__label">
                            First come first serve
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="waittime" className="form__label">
                          Dock Close Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckCloseTime
                          }
                          onChange={handleTimeChange}
                          property="DropDOckCloseTime"
                          fieldKey="DropDOckCloseTime"
                          error={errors.DropDOckCloseTime}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? true
                              : false
                          }
                        />
                        {errors.DropDOckCloseTime && (
                          <span className="form__error">{errors.DropDOckCloseTime}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Note</label>
                        <textarea
                          id="DropDOckNote"
                          name="DropDOckNote"
                          // className="form__textarea form__textarea--xs"
                          className={`form__textarea form__textarea--xs ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__textarea--disabled"
                            : ""
                            }`}
                          placeholder="Special Instructions"
                          autoComplete="off"
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckNote
                          }
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          onChange={handleTextAreaChange}
                          maxLength={255}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            ) : (
              ""
            )}

            {notLTL ? (
              <section className="form__section">
                <h1 className="form__section__title">
                  Select Additional Services
                </h1>
                <div className="formRow">
                  <div className="form__checkbox form__checkbox--gray">
                    <div className="form__checkbox__option mb_0">
                      <input
                        type="checkbox"
                        id="Remove & Disposal"
                        autoComplete="off"
                        className={`form__checkbox__input`}
                        checked={fields.additionalServices.some(
                          (x) => x === "Remove & Disposal"
                        )}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form__checkbox__label">
                        Remove & Disposal
                      </label>
                    </div>
                    <div className="form__checkbox__option mb_0">
                      <input
                        type="checkbox"
                        id="Order Swap & Exchange"
                        autoComplete="off"
                        className={`form__checkbox__input`}
                        checked={fields.additionalServices.some(
                          (x) => x === "Order Swap & Exchange"
                        )}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form__checkbox__label">
                        Order Swap & Exchange
                      </label>
                    </div>
                  </div>
                </div>
                <div className="formRow mb_0">
                  <div
                    className={
                      getQuote && quoteData?.length > 0
                        ? "btn__group"
                        : "btn__group mb_0"
                    }
                  >
                    <button
                      className="btn btn__success"
                      type="button"
                      onClick={() => {
                        getQuotesData();
                      }}
                      disabled={isQuote}
                    >
                      Get Quote
                    </button>
                  </div>
                </div>
                {getQuote &&
                  (quoteData?.length > 0 ? (
                    <>
                      {/* <GetTraditionalQuote
                  setFields={setFields}
                  quoteData={quoteData}               // raw traditional array (offeredProductList/primaryVendor/etc.)
                  setIsQuote={setIsQuote}
                  saveQuotation={handleSaveQuotation} // same handler you already pass
                  quoteSelectedId={stateQuoteId}
                  masterHubName={masterHubName}
                  masterHubAddress={masterHubAddress}
                /> */}
                    </>
                  ) : (
                    <span>
                      {orderTypeRowData?.name === "Final Mile"
                        ? "No Final Mile Agent Available!"
                        : orderTypeRowData?.name === "LTL"
                          ? "No LTL Agent Available!"
                          : orderTypeRowData?.name === "LTL + Final Mile"
                            ? "No LTL + Final Mile Agent Available!"
                            : "No Results Found!"}
                    </span>
                  ))}
              </section>
            ) : orderTypeRowData?.name !== "Expedited" ? (
              <section className="form__section">
                <div className="formRow formRow--gapNone">
                  <h1 className="form__section__title form__section__title--flex">
                    Add Handling Unit Details
                    <div className="btn__group mb_0">
                      <button
                        className="btn btn__white"
                        onClick={handleAddItemDetails}
                      >
                        <AddIcon />
                        Add Handling Units
                      </button>
                    </div>
                  </h1>

                  <OrderItemDetailTable
                    invalidCells={invalidCells}
                    invalidPackageCells={invalidPackageCells}
                    emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                    fieldKey={HANDLING_UNITS_FIELD_KEY}
                    focusCell={focusCell}
                    columns={tableConfig.columnsLTL}
                    packageType={
                      // orderTypeRowData?.name === "LTL" ||
                      //   orderTypeRowData?.name === "LTL + Final Mile"
                      //   ? PACKAGE_TYPE
                      //   : EXPEDIET_PACKAGE_TYPE
                      apiPackageType
                    }
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                    orderType={orderTypeRowData?.name}
                    customerId={assCustomerRowData?.id || customerRowData?.id}
                  />

                  {/* <div className="tableWrap tableWrap--order p_0">
                  <ItemDetailsTable
                    invalidCells={invalidCells}
                    fieldKey={HANDLING_UNITS_FIELD_KEY}
                    focusCell={focusCell}
                    columns={tableConfig.columnsLTL}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                  />
                </div> */}
                </div>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Density
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Linear Feet"
                      value={parseFloat(fields.totalDensity).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Linear Feet
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Linear Feet"
                      value={parseFloat(fields.totalLinearFeet).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Cubic Meter
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Cubic Meter"
                      value={parseFloat(fields.totalCBM).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="formRow">
                  <div
                    className={showLTLList ? "btn__group" : "btn__group mb_0"}
                  >
                    <button
                      className="btn btn__success"
                      type="button"
                      onClick={() => {
                        getTraditionalQuoteData();
                      }}
                    >
                      {"Get Quote"}
                    </button>
                  </div>
                </div>
                {showLTLList && (
                  <GetTraditionalQuote
                    orderType={
                      orderTypeRowData?.name === "LTL"
                        ? "LTL"
                        : "LTL + Final Mile"
                    }
                    setFields={setFields}
                    fields={fields}
                    quoteData={quoteData}
                    setIsQuote={setIsQuote}
                    setIsSelectedParent={setIsSelectedParent}
                    saveQuotation={
                      orderTypeRowData?.name === "LTL"
                        ? handleSaveQuotationForLTL
                        : handleSaveQuotation
                    }
                    formData={ltlDataToTransfer}
                    quoteSelectedId={stateQuoteId}
                    orderTypeRowData={orderTypeRowData}
                    setOrderTypeRowData={setOrderTypeRowData}
                    ltlFMData={ltlFMData}
                    setLtlFMData={setLtlFMData}
                    ltlAccessorialData={ltlAccessorialData}
                    savedData={savedData}
                    setQuoteType={setQuoteType}
                    isLtlOnly={isLtlOnly}
                    setIsLtlOnly={setIsLtlOnly}
                  />
                )}
                {getQuote &&
                  notLTL &&
                  (quoteData?.length > 0 ? (
                    <GetQuote
                      setFields={setFields}
                      quoteData={quoteData}
                      setIsQuote={setIsQuote}
                    />
                  ) : (
                    <span>No Results Found!</span>
                  ))}
              </section>
            ) : (
              ""
            )}
            <section className="form__section">
              {showFMList && notLTL && (
                <>
                  <div className="getQuoteBox" style={{ marginBottom: "15px" }}>
                    <p className="para">
                      {/* Hub: Hub Name C/O Agent Name, City, State, Zipcode, Country, Hours of operation, Mile */}
                      {`Hub: ${hasMasterHub ? masterHubName : fields?.hubName
                        }  C/O ${fields?.agentName || ""}, ${hasMasterHub ? masterHubAddress.city : fields?.city
                        }, ${hasMasterHub ? masterHubAddress.state : fields?.country
                        }, ${hasMasterHub
                          ? masterHubAddress.zipCode
                          : fields?.zipCode
                        }, ${hasMasterHub
                          ? masterHubAddress.country
                          : fields?.country
                        } | Hrs of Operation: ${hasMasterHub
                          ? masterHubAddress?.operationHrsFrom &&
                            masterHubAddress?.operationHrsTo
                            ? `${masterHubAddress.operationHrsFrom} - ${masterHubAddress.operationHrsTo}`
                            : ""
                          : fields?.operationHrsFrom && fields?.operationHrsTo
                            ? `${fields.operationHrsFrom} - ${fields.operationHrsTo}`
                            : ""
                        } | ${fields.distanceToDestination} mi`}
                    </p>
                    <h3 className="title title--h3">
                      Quote Price: $
                      {fields?.quotePrice
                        ? parseFloat(fields?.quotePrice).toFixed(2)
                        : 0.0}
                    </h3>
                  </div>
                </>
              )}
              {isQuote && notLTL && (
                <>
                  <h3 className="form__section__title">Mid Stop</h3>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopAddress" className="form__label">
                          Address
                        </label>
                        <input
                          type="text"
                          id="MidStopAddress"
                          name="MidStopAddress"
                          data-field="MidStopAddress"
                          className={`form__input form__input--sm form__input--disabled${fieldErrorClass("MidStopAddress")}`}
                          placeholder="Address"
                          autoComplete="off"
                          value={
                            masterHubAddress?.address
                              ? masterHubAddress?.address
                              : fields.MidStopAddress
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                        {errors.MidStopAddress && (
                          <span className="form__error">{errors.MidStopAddress}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopAPTSuit" className="form__label">
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="MidStopAPTSuit"
                          name="MidStopAPTSuit"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="APT/Suite"
                          autoComplete="off"
                          value={
                            masterHubAddress?.aptSuit
                              ? masterHubAddress?.aptSuit
                              : fields.MidStopAPTSuit
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopZipcode" className="form__label">
                          Zip Code
                        </label>
                        <input
                          type="text"
                          id="MidStopZipcode"
                          name="MidStopZipcode"
                          data-field="MidStopZipcode"
                          autoComplete="off"
                          className={`form__input form__input--sm form__input--disabled${fieldErrorClass("MidStopZipcode")}`}
                          placeholder="Zip Code"
                          value={
                            masterHubAddress?.zipCode
                              ? masterHubAddress?.zipCode
                              : fields.MidStopZipcode
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                        {errors.MidStopZipcode && (
                          <span className="form__error">{errors.MidStopZipcode}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopCity"
                              className="form__label"
                            >
                              City
                            </label>
                            <input
                              type="text"
                              id="MidStopCity"
                              name="MidStopCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={
                                masterHubAddress?.city
                                  ? masterHubAddress?.city
                                  : fields.MidStopCity
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="MidStopState"
                              name="MidStopState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={
                                masterHubAddress?.state
                                  ? masterHubAddress?.state
                                  : fields.MidStopState
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="MidStopCountry"
                              name="MidStopCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={
                                masterHubAddress?.country
                                  ? masterHubAddress?.country
                                  : fields.MidStopCountry
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopContactName"
                          className="form__label"
                        >
                          Contact Name
                        </label>
                        <input
                          type="text"
                          id="MidStopContactName"
                          name="MidStopContactName"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Contact name"
                          autoComplete="off"
                          value={
                            masterHubAddress?.contactPerson
                              ? masterHubAddress?.contactPerson
                              : fields.MidStopContactName
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopPhoneNumber"
                          className="form__label"
                        >
                          Phone Number
                        </label>
                        <div className="form__groupField form__groupField--phonenumber form__groupField--disabled">
                          <select
                            className="form__select form__select--sm"
                            disabled={true}
                            value={
                              masterHubAddress?.countryCallingCode
                                ? masterHubAddress?.countryCallingCode
                                : fields.MidStopPhoneNumberExt
                            }
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                MidStopPhoneNumberExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={
                                  masterHubAddress?.countryCallingCode
                                    ? masterHubAddress?.countryCallingCode
                                    : item?.countryCallingCode
                                }
                              >
                                {masterHubAddress?.countryCallingCode
                                  ? masterHubAddress?.countryCallingCode
                                  : item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            id="MidStopPhoneNumber"
                            name="MidStopPhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Phone Number"
                            value={onInputPhNumFormat(
                              masterHubAddress?.phoneNumber
                                ? masterHubAddress?.phoneNumber
                                : fields.MidStopPhoneNumber,
                              masterHubAddress?.countryCallingCode
                                ? masterHubAddress?.countryCallingCode
                                : fields.MidStopPhoneNumberExt || "+1"
                            )}
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            autoComplete="off"
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <EmailChips
                        emails={
                          masterHubAddress?.contactEmail
                            ? masterHubAddress?.contactEmail
                            : fields.MidStopContactEmail
                        }
                        setEmails={setFields}
                        placeholder="Contact Email"
                        label="Contact Email"
                        keyName="MidStopContactEmail"
                        error={errors}
                        setError={setErrors}
                        disable={true}
                      />
                    </div>
                  </div>
                </>
              )}
              {orderTypeRowData?.name === "Expedited" ? (
                <>
                  <div className="formRow formRow--gapNone">
                    <h1 className="form__section__title form__section__title--flex">
                      Add Handling Unit Details
                      <div className="btn__group">
                        <button
                          className="btn btn__white"
                          onClick={handleAddItemDetails}
                        >
                          <AddIcon />
                          Add Handling Units
                        </button>
                      </div>
                    </h1>
                    <OrderItemDetailTable
                      invalidCells={invalidCells}
                      invalidPackageCells={invalidPackageCells}
                      emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                      fieldKey={HANDLING_UNITS_FIELD_KEY}
                      focusCell={focusCell}
                      columns={tableConfig.columnsLTL}
                      packageType={apiPackageType}
                      data={data}
                      setData={setData}
                      setLoading={setLoading}
                      onDeleteConfig={tableConfig.delete}
                      showDelete={true}
                      isLTL={true}
                      isEditable={true}
                      setFields={setFields}
                      orderType={orderTypeRowData?.name}
                      customerId={assCustomerRowData?.id || customerRowData?.id}
                    />
                  </div>
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneePhoneNumber"
                          className="form__label"
                        >
                          Total Cubic Meter
                        </label>
                      </div>
                      <input
                        type="text"
                        id="MidStopAPTSuit"
                        name="MidStopAPTSuit"
                        autoComplete="off"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Total Cubic Meter"
                        value={parseFloat(fields.totalCBM).toFixed(3)}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <h3 className="form__section__title">
                    Courier / On Demand Agent Details
                  </h3>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={agentOrderColumns}
                        data={agentNameDropdown}
                        setData={setAgentNameDropdown}
                        captionTitles="Select Agent"
                        rowData={agentRowData}
                        setRowData={(row: any) => {
                          setAgentRowData(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.agent);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.agent}
                        error={errors[DROPDOWN_FIELD_KEYS.agent]}
                        displayColumn="name"
                        apiUrl={"/agent"}
                        labelTitle="Agent Name"
                        enableRemove={true}
                        mandatory={true}
                        type={"dropdown"}
                      />
                    </div>
                    <div className="formCol">
                      <MutliDropdown
                        labelTitle="Agent Hub"
                        columns={agentHubColumns}
                        data={agentHubDropdownData}
                        setData={setAgentHubDropdownData}
                        captionTitles="Select Agent Hub"
                        rowData={agentHubRowData}
                        setRowData={(row: any) => {
                          setAgentHubRowData(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.agentHub);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.agentHub}
                        error={errors[DROPDOWN_FIELD_KEYS.agentHub]}
                        displayColumn="name"
                        apiUrl={agentRowData?.id ? "/agent/agent-hub" : ""}
                        type={"dropdown"}
                        extraParentId={{
                          key: "agentId",
                          id: agentRowData?.id ? agentRowData?.id : "",
                        }}
                        enableRemove={true}
                        mandatory={
                          agentRowData?.agentType &&
                            agentRowData?.agentType != "LTL"
                            ? true
                            : false
                        }
                        extendedUrl={{
                          from: "dropdown",
                        }}
                        extraFields={[
                          "isVirtualHub",
                          "masterHubId",
                          "masterHubName",
                        ]}
                      />
                      {agentHubRowData?.isVirtualHub &&
                        String(agentHubRowData.masterHubId).trim() !== "" && (
                          <span className="form__error">
                            Note: This hub belongs to Master Hub :{" "}
                            {agentHubRowData.masterHubName || "—"}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Origin City</label>
                        <input
                          type="text"
                          id="originCity"
                          name="originCity"
                          className="form__input form__input--sm"
                          placeholder="Origin City"
                          autoComplete="off"
                          value={fields.originCity}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopCity" className="form__label">
                          Service Cost <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="number"
                          id="serviceCost"
                          name="serviceCost"
                          data-field="serviceCost"
                          className={`form__input form__input--sm${fieldErrorClass("serviceCost")}`}
                          placeholder="Service Cost"
                          autoComplete="off"
                          value={fields.serviceCost}
                          onChange={handleFieldChange}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.serviceCost && (
                          <span className="form__error">{errors.serviceCost}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Courier Pro. No. 1
                        </label>
                        <input
                          type="text"
                          id="courierPro1"
                          name="courierPro1"
                          className="form__input form__input--sm"
                          placeholder="Courier Pro. No. 1"
                          autoComplete="off"
                          value={fields.courierPro1}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopPhoneNumber"
                          className="form__label"
                        >
                          Courier Pro. No. 2
                        </label>
                        <input
                          type="text"
                          id="courierPro2"
                          name="courierPro2"
                          className="form__input form__input--sm"
                          placeholder="Courier Pro. No. 2"
                          autoComplete="off"
                          value={fields.courierPro2}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                ""
              )}
              <div className="btn__group">
                <button
                  className="btn btn__white"
                  type="button"
                  onClick={handleBack}
                >
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  type="submit"
                  onClick={handleSave}
                  // Picking a quote is a step, not a field the user can fill in place,
                  // so it stays a disabled button rather than a validation message.
                  disabled={!isSelectedParent}
                >
                  Book
                </button>
                {((isQuote && orderTypeRowData?.name != "LTL") ||
                  orderTypeRowData?.name === "Expedited") && (
                    <button
                      className="btn btn__primary"
                      type="submit"
                      onClick={() => {
                        setViewAddDocumentModal(true);
                      }}
                    >
                      Add Document & URL
                    </button>
                  )}
              </div>
            </section>
          </div>
        </div>
        {viewAddDocumentModal && (
          <AddDocumentURLModal
            confirmModal={viewAddDocumentModal}
            setConfirmModal={setViewAddDocumentModal}
            setDocumentInfo={setDocumentInfo}
            documentInfo={documentInfo}
          />
        )}
        {showModal && (
          <ConfirmModal
            confirmInfo={{
              heading: "Unsaved Changes!",
              para: "You have not saved your changes, do you want to continue",
            }}
            onConfirm={handleConfirmNavigation}
            setConfirmModal={handleCancelNavigation}
            firstMessageShow={true}
          />
        )}
      </div>
    </>
  );
};

export default TraditionalOrder;
